import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Info, Trash2, Repeat, Edit3, Pencil, ArrowDownAZ, ArrowDownZA, UserCheck, GripVertical } from 'lucide-react';
import { PersonMultiPicker } from '@synotis/ui';
import {
  apiSchedule,
  apiAffaires,
  apiAllocations,
  apiPhases,
  apiTimeOffs,
  apiTimeOffTypes,
  apiPersonManagers,
  apiPersons,
  apiSettings,
  apiWorkLocations,
} from '../api/client.js';
import TaskPicker from '../components/TaskPicker.jsx';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import AffaireFilterableSelect from '../components/AffaireFilterableSelect.jsx';
import { lastAffaireIn, rememberLastAffaire } from '../lib/lastAffaire.js';
import EditScopeDialog from '../components/EditScopeDialog.jsx';
import { buildModeCursor, ToolModeBadge, ToolModeContextMenu } from '../components/ToolModeMenu.jsx';
import RecurrencePicker, { buildRRuleClient, parseRRuleClient } from '../components/RecurrencePicker.jsx';
import FilterBar from '../components/FilterBar.jsx';
import TimelineProgress from '../components/TimelineProgress.jsx';
import DensityPicker, {
  DENSITY_ROW_HEIGHT,
  readDensity,
  writeDensity,
} from '../components/DensityPicker.jsx';
import { useFilterStore, toFilterQuery, hasNonSelfFilter } from '../hooks/useFilterStore.js';
import { useSlidingTimeline } from '../hooks/useSlidingTimeline.js';
import { useStableHandler } from '../hooks/useStableHandler.js';
import {
  useRangeData,
  mergeByKey,
  mergeHolidays,
  mergePersons,
  pruneHolidays,
  spanInRange,
  dateInRange,
} from '../hooks/useRangeData.js';
import { SCHEDULE_PERSON_SORT, useSortStore } from '../hooks/useSortStore.js';
import PersonFormModal from '../components/PersonFormModal.jsx';
import Avatar from '../components/Avatar.jsx';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { halfDayLabel, gescomRequestUrl, dayCoverageFactor } from '../components/HalfDayPicker.jsx';
import { MAX_HOURS_PER_DAY, normalizeHourStep, snapHours } from '../lib/hourStep.js';

/**
 * Type de congé réservé aux sous-traitants — même libellé que
 * `TIMEOFF_TYPE_UNAVAILABILITY` côté API, qui reste l'autorité.
 */
const UNAVAILABILITY_TYPE_NAME = 'Indisponibilité';
// #86 — miroir de `TIMEOFF_TYPE_NOT_IN_EXPORT` : seul type de congé encore
// créable dans Synolog (les autres se déposent dans Gescom), validé d'office,
// supprimable par son auteur quel que soit le statut. L'API reste l'autorité.
const PART_TIME_TYPE_NAME = 'Temps Partiel';

/**
 * Sprint 2.B — Vue Schedule (lecture seule virtualisée).
 * Sprint 2.C — Drag-to-create d'allocations (ADMIN uniquement).
 * Sprint 2.D — Drag-to-move + drag-to-resize + pointer events (touch) +
 *              zoom mois + phase picker + édition d'allocation.
 *
 * Patterns Float repris (analyse/float-tech-patterns-report.md +
 * analyse/float-drag-selection-architecture.md) :
 *  - §7 Virtualisation horizontale + verticale (rendu uniquement des jours et
 *    lignes visibles, buffer de 3 jours / 3 lignes).
 *  - §8 Sticky pinning : col gauche + header haut.
 *  - §4 `data-*` attributes comme état UI déclaratif (`data-is-today`,
 *    `data-row-type`, `data-floatid`, `data-is-dragging`, `data-is-moving`,
 *    `data-is-resizing`, `data-is-any-row-dragging`).
 *  - Drag-selection : `onPointerDown` (ancre) + `onPointerEnter` (étend) +
 *    `pointerup` global (commit). Overlay de sélection unique en absolute,
 *    `pointer-events: none` pour laisser passer aux cellules en dessous.
 *  - Annexe A : `setPointerCapture(pointerId)` pour garantir que les events
 *    continuent à arriver même si le doigt/stylet sort de la cellule de départ.
 *
 * Le clic simple sur un block ouvre :
 *  - une modale info en lecture seule pour USER/VIEWER,
 *  - une modale d'édition complète pour ADMIN.
 */

// ---------- Constantes de mise en page ----------
// Largeur de jour minimale par zoom (plancher du fit-to-width ci-dessous).
// `dayWidth` s'étire pour que les jours VISIBLES (cf. PERIOD_BY_ZOOM) remplissent
// la largeur dispo, sans jamais descendre sous ce plancher.
const MIN_DAY_WIDTH = {
  day: 40, // ~lisible au jour près
  week: 16,
  month: 7,
};

// Sprint 8.4 — Fenêtre temporelle visible selon le zoom. Sans ça, en
// zoom mois on a 35 jours × 10 px ≈ 350 px de contenu pour une viewport de
// 1500-1700 px → moitié vide. On dimensionne chaque preset pour que
// `visibleDays × dayWidth` couvre ~1700 px (largeur typique du calendrier
// après LEFT_COL_WIDTH).
//  - visibleDays : nombre de jours affichés à l'écran (fixe la largeur d'un
//                  jour, donc la densité — inchangée par le ticket #9)
//  - backDays    : nombre de jours avant aujourd'hui au premier affichage
//  - shiftDays   : pas de navigation des flèches ← / →. Ticket #91 : une
//                  semaine au minimum — un pas d'un jour en zoom Jour rendait
//                  la navigation laborieuse ; touche maintenue = défilement
//                  continu (cf. useSlidingTimeline).
//
// Ticket #9 / #91 — la fenêtre CHARGÉE fait cinq écrans (deux de marge de
// chaque côté, cf. `WINDOW_FACTOR` dans useSlidingTimeline) et glisse d'un
// écran à l'approche d'un bord. Reste sous la borne serveur de 365 jours
// (70 × 5 = 350 en zoom Mois).
const PERIOD_BY_ZOOM = {
  day:   { visibleDays: 7,  backDays: 0,  shiftDays: 7 },  // 1 sem affichée, pas d'1 semaine
  week:  { visibleDays: 21, backDays: 7,  shiftDays: 7 },  // 3 sem affichées, pas d'1 semaine
  month: { visibleDays: 70, backDays: 7,  shiftDays: 30 }, // 10 sem affichées, pas d'1 mois
};

// ---------- Ticket #91 — fusion des segments chargés ----------
const EMPTY_SCHEDULE = {
  persons: [],
  allocations: [],
  timeOffs: [],
  holidaysByPerson: {},
  workLocations: [],
};
const instanceKey = (x) => x.instanceId ?? x.id;
const EMPTY_LIST = [];

/** Réutilise le tableau précédent d'une clé quand il contient exactement les
 *  mêmes éléments (mêmes références, même ordre). */
function reuseUnchangedLists(next, prev) {
  for (const [k, list] of next) {
    const old = prev.get(k);
    if (old && old.length === list.length && old.every((x, i) => x === list[i])) next.set(k, old);
  }
  return next;
}
const workLocationKey = (w) => `${w.personId}|${String(w.date).slice(0, 10)}|${w.slot}`;

function mergeScheduleData(prev, next) {
  return {
    ...prev,
    ...next,
    persons: mergePersons(prev.persons, next.persons),
    allocations: mergeByKey(prev.allocations, next.allocations, instanceKey),
    timeOffs: mergeByKey(prev.timeOffs, next.timeOffs, instanceKey),
    holidaysByPerson: mergeHolidays(prev.holidaysByPerson, next.holidaysByPerson),
    workLocations: mergeByKey(prev.workLocations, next.workLocations, workLocationKey),
  };
}

function pruneScheduleData(d, from, to) {
  return {
    ...d,
    allocations: (d.allocations ?? []).filter((a) => spanInRange(a, from, to)),
    timeOffs: (d.timeOffs ?? []).filter((t) => spanInRange(t, from, to)),
    holidaysByPerson: pruneHolidays(d.holidaysByPerson, from, to),
    workLocations: (d.workLocations ?? []).filter((w) => dateInRange(w, from, to)),
  };
}
const ROW_HEIGHT_PX = 80;
const LEFT_COL_WIDTH_PX = 220;
const HEADER_HEIGHT_PX = 56;
// Ticket #50 — hauteur de la bande « mois » affichée en haut du header, à tous
// les zooms (façon Float) : sans elle on ne lit que des numéros de jour.
const MONTH_BAND_HEIGHT_PX = 20;
const BUFFER_DAYS = 3;
// Ticket #91 — granularité de la plage de jours rendue (cf. `firstVisibleDay`).
const RENDER_CHUNK_DAYS = { day: 7, week: 7, month: 14 };
const BUFFER_ROWS = 3;
const BLOCK_VERTICAL_PAD = 6;
// Hauteur du bandeau de statut de présence (.wl-band, cf. DRAG_STYLE_CSS) :
// réservée en haut de chaque ligne pour que les blocs d'allocation/congé ne
// soient pas recouverts par le statut (le bandeau est en z-index 6 > bloc 4).
const WL_BAND_HEIGHT = 14;

// Largeur des poignées de resize (zone cliquable invisible).
const RESIZE_HANDLE_WIDTH_PX = 6;

// Auto-scroll au bord pendant un drag (analyse §A).
const AUTOSCROLL_EDGE_PX = 40;
const AUTOSCROLL_SPEED_PX = 10;

// ---------- Helpers dates ----------
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseISODate(s) {
  // Évite les surprises de fuseau horaire — on prend la date locale 00:00.
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function diffDays(a, b) {
  return Math.round((a.getTime() - b.getTime()) / MS_PER_DAY);
}

function formatISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

const FULL_DAY_HOURS = 8;

/**
 * Ticket #48 — seuls ces congés se redimensionnent verticalement : les autres
 * types (partiels compris) viennent de Gescom, qui en est le maître.
 */
const HOURS_RESIZABLE_TIMEOFF_TYPES = new Set(['Temps Partiel', 'Indisponibilité']);

/**
 * Un congé occupe-t-il la journée entière ? `fullDay === true`, OU un congé
 * marqué `fullDay=false` mais dont les heures couvrent la capacité d'un jour
 * plein (≥ 8h) — cas Float où un congé « journée » est enregistré en 8h/j sans
 * le flag fullDay. Ces congés **cassent** l'allocation chevauchée (ils ne
 * coexistent pas en voie séparée comme un vrai congé partiel de 4h).
 */
function isFullDayLeave(t) {
  return t.fullDay === true || (Number(t.hoursPerDay) || 0) >= FULL_DAY_HOURS;
}

function fmtDayShort(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

/**
 * Sprint 24 — détection de surbooking (front, non bloquant côté backend).
 *
 * Calcule, pour `personId` sur la plage [startIso, endIso] de l'allocation en
 * cours de création/déplacement/redimensionnement, les jours où le total des
 * heures allouées (existant + nouvelle, en excluant l'allocation déplacée)
 * dépasse la capacité disponible (8h − férié(8h) − congés approuvés du jour).
 *
 * Renvoie un tableau de `{ iso, allocated, capacity }` pour les jours en
 * dépassement (vide si pas de surbooking). Robuste aux données manquantes :
 * en cas d'absence d'allocations, renvoie [] (pas de modale → on procède).
 *
 * @param ctx { allocations, timeOffsByPerson, holidaysByPersonIndex }
 * @param excludeId allocation.id à exclure (déplacement/resize de soi-même)
 */
function computeOverbookedDays({
  personId,
  startIso,
  endIso,
  newHoursPerDay,
  ctx,
  excludeId = null,
}) {
  if (!personId || !startIso || !endIso) return [];
  const hours = Number(newHoursPerDay);
  if (!Number.isFinite(hours) || hours <= 0) return [];

  const allocations = ctx.allocations ?? [];
  const toList = ctx.timeOffsByPerson?.get(personId) ?? [];
  const holidayMap = ctx.holidaysByPersonIndex?.get(personId) ?? null;

  const start = parseISODate(startIso);
  const end = parseISODate(endIso);
  if (end.getTime() < start.getTime()) return [];

  const overbooked = [];
  for (let d = new Date(start); d.getTime() <= end.getTime(); d = addDays(d, 1)) {
    const iso = toISODate(d);

    // Capacité du jour : 8h − férié(8h) − congés approuvés.
    let capacity = FULL_DAY_HOURS;
    if (holidayMap?.has(iso)) capacity = 0;
    if (capacity > 0) {
      for (const t of toList) {
        if (t.status && t.status !== 'APPROVED') continue;
        const ts = parseISODate(t.startDate);
        const te = parseISODate(t.endDate);
        if (d.getTime() < ts.getTime() || d.getTime() > te.getTime()) continue;
        if (t.fullDay !== false) capacity = 0;
        else capacity -= Number(t.hoursPerDay) || 0;
      }
    }
    capacity = Math.max(0, capacity);

    // Total alloué ce jour : somme des allocations actives de la personne
    // chevauchant ce jour (en excluant l'allocation déplacée), + la nouvelle.
    let allocated = hours;
    for (const a of allocations) {
      if (a.personId !== personId) continue;
      if (excludeId && a.id === excludeId) continue;
      const as = parseISODate(a.startDate);
      const ae = parseISODate(a.endDate);
      if (d.getTime() < as.getTime() || d.getTime() > ae.getTime()) continue;
      const h = Number(a.hoursPerDay);
      if (Number.isFinite(h) && h > 0) allocated += h;
    }

    if (allocated > capacity) overbooked.push({ iso, allocated, capacity });
  }
  return overbooked;
}

/**
 * Masque des jours ouvrés d'une Person (depuis `workDaysHours` Float).
 * Retourne un tableau indexé par `Date.getDay()` (0=dim, 1=lun, ..., 6=sam),
 * `true` = jour travaillé. Fallback semaine 5j (lun-ven) si absent.
 *
 * Deux formats acceptés :
 *  - Objet `{ monday: 8, tuesday: 8, ..., sunday: 0 }` (nominal Synolog).
 *  - Array Float legacy de 7 entrées `[dim, lun, mar, mer, jeu, ven, sam]`
 *    (US standard, directement aligné sur `Date.getDay()`).
 */
function getWorkingDayMask(workDaysHours) {
  const DEFAULT_5J = [false, true, true, true, true, true, false];
  if (!workDaysHours) return DEFAULT_5J;
  let mask;
  if (Array.isArray(workDaysHours)) {
    if (workDaysHours.length !== 7) return DEFAULT_5J;
    mask = workDaysHours.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    });
  } else if (typeof workDaysHours !== 'object') {
    return DEFAULT_5J;
  } else {
    const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    mask = keys.map((k) => {
      const v = Number(workDaysHours[k]);
      return Number.isFinite(v) && v > 0;
    });
  }
  // Sprint 16 fix — un `workDaysHours` vide (`{}`) ou entièrement à zéro produit
  // un masque sans AUCUN jour travaillé → `splitAllocationIntoSegments`
  // supprimerait alors tous les segments et masquerait toutes les allocations de
  // la Person (bug observé sur une fiche fraîchement activée / non configurée).
  // On retombe sur la semaine 5j par défaut, comme pour un `workDaysHours` absent.
  if (!mask.some(Boolean)) return DEFAULT_5J;
  return mask;
}

function isInRange(t, ranges) {
  for (const r of ranges) {
    if (r.startMs <= t && t <= r.endMs) return true;
  }
  return false;
}

/**
 * Fragmente une allocation `[startDate, endDate]` en plusieurs segments
 * contigus, en excluant :
 *  - Les jours non ouvrés (selon `workingDayMask`, p.ex. samedi/dimanche)
 *  - Les jours couverts par un TimeOff (priorité TimeOff sur Allocation)
 *  - Les jours fériés de la Person (Sprint 6.1 — priorité férié sur Allocation)
 *
 * Retourne `[{ startDate, endDate }]` (chaque segment = N jours contigus).
 *
 * Exemple : alloc lun 5→ven 16 mai + WE skip + TimeOff jeu 8→mar 13 mai →
 *   segments = [{ 5→7 }, { 14→16 }]
 *
 * @param holidayDateSet  Set/Map d'isoDates (`YYYY-MM-DD`) à exclure (méthode
 *                        `.has(iso)`). Optionnel — passer `null` pour ignorer.
 */
function splitAllocationIntoSegments(allocation, workingDayMask, timeOffRanges, holidayDateSet) {
  const start = parseISODate(allocation.startDate);
  const end = parseISODate(allocation.endDate);
  const segments = [];
  let segStart = null;
  let segEnd = null;
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    const dow = cursor.getDay();
    const t = cursor.getTime();
    const iso = formatISODate(cursor);
    const isHoliday = holidayDateSet ? holidayDateSet.has(iso) : false;
    const included = workingDayMask[dow] && !isInRange(t, timeOffRanges) && !isHoliday;
    if (included) {
      if (!segStart) segStart = new Date(cursor);
      segEnd = new Date(cursor);
    } else if (segStart) {
      segments.push({ startDate: formatISODate(segStart), endDate: formatISODate(segEnd) });
      segStart = null;
      segEnd = null;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  if (segStart) {
    segments.push({ startDate: formatISODate(segStart), endDate: formatISODate(segEnd) });
  }
  return segments;
}

function startOfWeek(d) {
  // Lundi de la semaine de `d` (locale FR — lundi = 1).
  const day = (d.getDay() + 6) % 7; // 0=lundi, 6=dimanche
  return addDays(d, -day);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Numéro de semaine ISO 8601 (lundi premier jour, semaine 1 = celle du 4 janvier). */
function isoWeekNum(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - day + 3); // jeudi de la semaine
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const fd = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - fd + 3);
  return 1 + Math.round((t - firstThu) / (7 * 86400000));
}

function isWeekend(d) {
  const day = d.getDay();
  return day === 0 || day === 6;
}

/**
 * Convention RH française : le nom de famille s'affiche TOUJOURS en
 * majuscules. La BDD conserve la casse d'origine (Float renvoie tantôt
 * "Bondaz" tantôt "BOYER") — l'uppercase est appliqué uniformément au
 * rendu uniquement. Le tri reste insensible à la casse via
 * `localeCompare(... sensitivity: 'base')`.
 */
function displayPersonName(person) {
  if (!person) return '—';
  const first = person.firstName ?? '';
  const last = (person.lastName ?? '').toUpperCase();
  return `${first} ${last}`.trim();
}

const MONTHS_FR = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];
const MONTHS_FR_LONG = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const DAYS_FR_SHORT = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DAYS_FR_LONG = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function fmtDate(d) {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}

// Ticket #50 — libellé de la période réellement VISIBLE (pas la fenêtre
// chargée, cinq fois plus large : l'intervalle affiché paraîssait faux).
// On évite aussi de répéter mois et année quand les deux bornes les partagent.
// Ticket #50 — libellé du ou des mois couverts par une plage (une semaine, en
// pratique). Quand la semaine est à cheval sur deux mois, les deux sont
// affichés séparés par un tiret, comme sur Float.
function fmtMonthSpan(from, to, short = false) {
  const M = short ? MONTHS_FR : MONTHS_FR_LONG;
  const yr = (d) => (short ? String(d.getFullYear()).slice(2) : String(d.getFullYear()));
  const sameMonth =
    from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
  if (sameMonth) return `${M[from.getMonth()]} ${yr(to)}`;
  if (from.getFullYear() === to.getFullYear()) {
    return `${M[from.getMonth()]} – ${M[to.getMonth()]} ${yr(to)}`;
  }
  return `${M[from.getMonth()]} ${yr(from)} – ${M[to.getMonth()]} ${yr(to)}`;
}

function fmtPeriodTitle(from, to) {
  const sameYear = from.getFullYear() === to.getFullYear();
  if (sameYear && from.getMonth() === to.getMonth()) {
    return `${from.getDate()} → ${to.getDate()} ${MONTHS_FR[to.getMonth()]} ${to.getFullYear()}`;
  }
  const left = sameYear ? fmtDate(from) : `${fmtDate(from)} ${from.getFullYear()}`;
  return `${left} → ${fmtDate(to)} ${to.getFullYear()}`;
}

// ---------- Couleurs ----------
const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

function affaireColor(a) {
  // Cascade : Affaire.color → Client.color → couleur par défaut.
  // Colonne `color` côté BDD est sans `#` (cf. import Float).
  const raw = a?.color || a?.client?.color;
  if (!raw) return 'var(--accent)';
  return raw.startsWith('#') ? raw : `#${raw}`;
}

function timeOffColor(t) {
  return t?.colorHex || 'var(--warning)';
}

/**
 * Ticket #20 — lisibilité des blocs colorés. Le texte était codé en dur en
 * blanc : sur un fond clair (gris/pastel choisi comme couleur de type d'absence
 * ou d'affaire), le contraste tombe sous le seuil AA. On choisit ici la couleur
 * de texte selon la luminance relative du fond.
 *
 * `bg` peut être une variable CSS (`var(--warning)`) non parsable côté JS :
 * dans ce cas on garde le blanc (les couleurs du thème sont sombres).
 */
function readableTextOn(bg) {
  const hex = typeof bg === 'string' ? bg.trim() : '';
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!m) return { color: '#fff', onLight: false };
  const raw = m[1];
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  // Luminance relative WCAG.
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Contraste vs blanc = 1.05 / (L + 0.05) ; on bascule sur un texte sombre
  // dès que le blanc passe sous ~4.5:1.
  return L > 0.18 ? { color: '#111827', onLight: true } : { color: '#fff', onLight: false };
}

// CSS injecté une fois pour les data-attrs drag-state (analyse §4).
const DRAG_STYLE_ID = 'schedule-drag-style';
const DRAG_STYLE_CSS = `
[data-is-any-row-dragging="true"] [data-row-type="person"]:not([data-is-dragging="true"]) {
  opacity: 0.5;
  transition: opacity 80ms linear;
}
[data-is-any-row-dragging="true"] {
  cursor: ew-resize;
  user-select: none;
  touch-action: none;
}
[data-row-type="day-cell"][data-can-drag="true"] {
  cursor: crosshair;
}
[data-row-type="allocation"][data-can-edit="true"] {
  cursor: grab;
}
[data-row-type="allocation"][data-is-moving="true"] {
  cursor: grabbing;
  opacity: 0.85;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4) !important;
  z-index: 10 !important;
}
[data-row-type="allocation"][data-is-resizing="true"],
[data-row-type="allocation"][data-is-vresizing="true"],
[data-row-type="timeoff"][data-is-vresizing="true"] {
  z-index: 10 !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4) !important;
}
.alloc-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: ${RESIZE_HANDLE_WIDTH_PX}px;
  cursor: col-resize;
  background: transparent;
  z-index: 6;
  touch-action: none;
}
/* Ticket #48 — poignée BASSE : ajuste les heures/jour (hauteur du bloc). */
.alloc-handle--v {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: auto;
  width: auto;
  height: ${RESIZE_HANDLE_WIDTH_PX}px;
  cursor: ns-resize;
  background: transparent;
  z-index: 7;
  touch-action: none;
}
[data-row-type="allocation"][data-can-edit="true"]:hover .alloc-handle {
  background: rgba(255,255,255,0.4);
}
[data-row-type="allocation"][data-can-edit="true"] .alloc-handle:hover {
  background: rgba(255,255,255,0.7);
}
[data-row-type="timeoff"][data-can-resize="true"]:hover .alloc-handle {
  background: rgba(255,255,255,0.4);
}
[data-row-type="timeoff"][data-can-resize="true"] .alloc-handle:hover {
  background: rgba(255,255,255,0.7);
}
/* Pendant un resize vertical, le curseur ns-resize prime sur le ew-resize
   global posé par [data-is-any-row-dragging]. */
[data-is-any-row-vresizing="true"], [data-is-any-row-vresizing="true"] * {
  cursor: ns-resize !important;
}
/* Ticket #6 — bandeau statut de présence (haut de chaque jour). */
.wl-band {
  position: absolute;
  top: 0;
  left: 0;
  height: 14px;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  line-height: 14px;
  color: #fff;
  overflow: hidden;
  white-space: nowrap;
  box-sizing: border-box;
}
.wl-band--empty {
  opacity: 0.4;
  color: var(--text-muted);
  background: var(--glass) !important;
  border-bottom: 1px dashed var(--glass-border);
  transition: opacity 80ms, background 80ms;
}
[data-row-type="day-cell"]:hover .wl-band--empty {
  opacity: 1;
  background: var(--accent) !important;
  color: #fff;
}
`;

function ensureDragStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(DRAG_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = DRAG_STYLE_ID;
  el.textContent = DRAG_STYLE_CSS;
  document.head.appendChild(el);
}

// ---------- Toast minimal ----------

function Toast({ message, kind = 'error', onClose }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => onClose?.(), 5000);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  const bg = kind === 'error' ? 'var(--danger)' : kind === 'success' ? 'var(--success)' : 'var(--accent)';
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 2000,
        background: bg,
        color: '#fff',
        padding: '10px 16px',
        borderRadius: 'var(--radius-sm)',
        boxShadow: 'var(--shadow-card)',
        fontSize: 13,
        maxWidth: 360,
      }}
    >
      {message}
    </div>
  );
}

// ---------- Modes outil (ticket #62) ----------
// Choisis au clic droit sur la grille, comme dans la saisie de temps.
// « Suppression » ne vise que les allocations : les congés sont gérés par
// Gescom et ne sont jamais supprimés depuis le Planning.
const TOOL_MODES = [
  { key: 'edit', label: 'Édition', Icon: Pencil, color: 'var(--accent)' },
  { key: 'delete', label: 'Suppression', Icon: Trash2, color: 'var(--danger)' },
];
const DELETE_CURSOR = buildModeCursor(`<path d='M20 20 l6 6 M26 20 l-6 6'/>`, '#dc2626');

// ---------- Composant principal ----------

/**
 * Ticket #49 — `initialDate` : ouverture du planning centrée sur un jour
 * cliqué depuis le détail d'une tuile d'accueil (jour non planifié). Sans
 * cette prop, la vue s'ouvre sur aujourd'hui comme auparavant.
 */
export default function ScheduleView({ user, initialDate }) {
  // Ancre d'ouverture : le jour ciblé (#49) ou aujourd'hui.
  const openAnchor = useMemo(() => {
    const base = initialDate ? new Date(`${initialDate}T00:00:00`) : TODAY;
    return addDays(startOfWeek(Number.isNaN(base.getTime()) ? TODAY : base), -PERIOD_BY_ZOOM.day.backDays);
  }, [initialDate]);
  const isAdmin = user?.role === 'ADMIN';

  // Sprint 2.D : 3ème niveau de zoom 'month'.
  const [zoom, setZoom] = useState('day'); // 'day' | 'week' | 'month'

  // Sprint 12 — densité d'affichage (hauteur des lignes), partagée avec le
  // Logtime calendaire via localStorage. Compact = hauteur historique.
  const [density, setDensity] = useState(() => readDensity());
  // Hauteur d'une voie (lane). La hauteur d'une ligne personne = laneHeight ×
  // nombre de voies (chevauchements simultanés) — calculé plus bas.
  const laneHeight = DENSITY_ROW_HEIGHT[density] ?? ROW_HEIGHT_PX;
  const changeDensity = useCallback((d) => {
    setDensity(d);
    writeDensity(d);
  }, []);

  // Toggle "Afficher seulement les actifs" — masque les Persons dont la
  // `endDate` (date de sortie) est strictement antérieure au lundi de la
  // semaine courante. Une personne sortie en cours de semaine reste visible
  // jusqu'au dimanche, après quoi elle disparaît. Persisté en localStorage.
  const [showActiveOnly, setShowActiveOnly] = useState(() => {
    try {
      const raw = localStorage.getItem('synolog_schedule_active_only');
      if (raw === null) return true;
      return raw === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('synolog_schedule_active_only', showActiveOnly ? '1' : '0');
    } catch {
      /* localStorage indisponible — état conservé en mémoire. */
    }
  }, [showActiveOnly]);

  // Sprint 8.3 — tri sur la colonne "Personnes". 4 états cycliques :
  //   { lastName, asc } → { lastName, desc } → { firstName, asc } → { firstName, desc } → cycle
  // Persisté en localStorage (clé `synolog_schedule_person_sort`).
  // Rétro-compatibilité v1 : si l'ancienne valeur 'asc' / 'desc' est lue,
  // elle est promue à `{ field: 'lastName', dir }`.
  // Ticket #37 — l'état vit dans `useSortStore` (partagé + persisté) pour qu'une
  // vue sauvegardée puisse le mémoriser et le réappliquer.
  const { sort: personSort, setSort: setPersonSort } = useSortStore(SCHEDULE_PERSON_SORT);

  // #19 — Ordre manuel des personnes (drag & drop). Quand défini, il PRIME sur
  // le tri ; un clic sur l'en-tête de tri le réinitialise. Persisté localement.
  const [personOrder, setPersonOrder] = useState(() => {
    try {
      const raw = localStorage.getItem('synolog_schedule_person_order');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (personOrder) localStorage.setItem('synolog_schedule_person_order', JSON.stringify(personOrder));
      else localStorage.removeItem('synolog_schedule_person_order');
    } catch {
      /* ignore */
    }
  }, [personOrder]);
  const togglePersonSort = useCallback(() => {
    setPersonOrder(null); // un tri explicite annule l'ordre manuel (#19)
    setPersonSort((s) => {
      if (s.field === 'lastName' && s.dir === 'asc') return { field: 'lastName', dir: 'desc' };
      if (s.field === 'lastName' && s.dir === 'desc') return { field: 'firstName', dir: 'asc' };
      if (s.field === 'firstName' && s.dir === 'asc') return { field: 'firstName', dir: 'desc' };
      return { field: 'lastName', dir: 'asc' };
    });
  }, []);
  // Geste en cours (drag create/move/resize) : ni glissement de fenêtre, ni
  // rechargement SSE sous le doigt. Alimenté plus bas depuis `isDragging`.
  const busyRef = useRef(false);

  // Ticket #91 — défilement temporel commun au Planning et à la vue calendaire
  // des imputations (fenêtre glissante, animation des flèches, clavier). La
  // taille de la fenêtre dépend du zoom (Sprint 8.4, cf. PERIOD_BY_ZOOM) ; le
  // jour d'ouverture est aligné sur le bord gauche (ticket #9).
  const {
    containerRef,
    period,
    totalDays,
    dayWidth,
    viewport,
    scrollLeft,
    scrollTop,
    navigate,
    anchorTo,
    containerProps,
  } = useSlidingTimeline({
    zoom,
    visibleDays: PERIOD_BY_ZOOM[zoom].visibleDays,
    shiftDays: PERIOD_BY_ZOOM[zoom].shiftDays,
    // Ticket #9 — la largeur d'un jour se calcule sur les jours VISIBLES : la
    // densité à l'écran est celle d'avant, mais le contenu déborde (vrai scroll).
    computeDayWidth: (available) =>
      Math.max(MIN_DAY_WIDTH[zoom], available / PERIOD_BY_ZOOM[zoom].visibleDays),
    leftColWidth: LEFT_COL_WIDTH_PX,
    initialAnchor: openAnchor,
    busyRef,
  });
  // Ticket #6 — modale de saisie des statuts de présence.
  const [statusModal, setStatusModal] = useState(null); // { personId, iso, current }

  // Modale détail / édition au clic sur un bloc.
  const [detail, setDetail] = useState(null); // { type, item, person }

  // Toast d'erreur (PATCH/DELETE/etc).
  const [toast, setToast] = useState(null); // { message, kind }
  // Sprint 24 — confirmation de surbooking : { days:[{iso,allocated,capacity}], personName, onConfirm }
  const [pendingOverbook, setPendingOverbook] = useState(null);

  // Ticket #62 — mode outil (clic droit) : `edit` (défaut) ou `delete`.
  const [toolMode, setToolMode] = useState('edit');
  const isDeleteMode = toolMode === 'delete';
  const [toolMenu, setToolMenu] = useState(null); // { x, y } | null
  const closeToolMenu = useCallback(() => setToolMenu(null), []);
  // Allocation récurrente en attente du choix de périmètre de suppression.
  const [pendingDelete, setPendingDelete] = useState(null);

  // ---------- Sprint 2.C — Drag-to-create ----------
  // drag = null OU { personId, anchorDayIndex, endDayIndex }
  const [drag, setDrag] = useState(null);
  // createDraft = null OU { personId, startDayIndex, endDayIndex }
  const [createDraft, setCreateDraft] = useState(null);
  // Cache des affaires actives pour le select dans les modales.
  const [affaires, setAffaires] = useState([]);
  const [affairesError, setAffairesError] = useState(null);

  // Sprint 16 — flag d'édition self-service du Schedule (lu pour les non-ADMIN ;
  // l'ADMIN édite toujours, indépendamment du flag).
  const [selfScheduleEnabled, setSelfScheduleEnabled] = useState(false);
  // #86 — flag self-service congés (suppression de son propre temps partiel).
  const [selfTimeOffEnabled, setSelfTimeOffEnabled] = useState(false);
  // Ticket #48 — pas des poignées d'heures (paramètre `logtimeMinHours`).
  const [hourStep, setHourStep] = useState(1);

  // ---------- Sprint 2.D — Drag-to-move ----------
  // move = null OU { allocationId, dragStartDayIdx, deltaDay }
  const [move, setMove] = useState(null);

  // ---------- Sprint 2.D — Drag-to-resize ----------
  // resize = null OU { allocationId, edge: 'left'|'right', originalStartIdx,
  //                    originalEndIdx, currentStartIdx, currentEndIdx }
  const [resize, setResize] = useState(null);

  // ---------- Ticket #48 — Drag-to-resize VERTICAL (heures/jour) ----------
  // Le resize horizontal ci-dessus change la période (dates). Ici on ajuste
  // `hoursPerDay` en tirant la poignée basse du bloc : la hauteur du bloc
  // étant proportionnelle à hoursPerDay / capacité, le geste est direct.
  // vresize = null OU { allocationId, instanceId, personId, originalHours,
  //                     currentHours, startClientY, laneHeight, dailyCapacity }
  const [vresize, setVresize] = useState(null);

  // CSS pour les data-attrs (injecté une fois côté DOM).
  useEffect(() => {
    ensureDragStyle();
  }, []);

  // Charge les affaires actives une fois.
  // En lecture seule on charge AUSSI les affaires : les USER/VIEWER ouvrent
  // une modale détail qui peut afficher l'affaire — mais on ne charge le
  // détail qu'à la demande. En pratique on ne charge que pour les ADMIN
  // (édition + création), USER/VIEWER se contentent du payload schedule.
  // Sprint 16 — charge le flag selfScheduleEnabled pour les non-ADMIN.
  // Ticket #48 — le pas des poignées d'heures sert à tous, ADMIN compris.
  useEffect(() => {
    let cancelled = false;
    apiSettings
      .get()
      .then((s) => {
        if (cancelled) return;
        setHourStep(normalizeHourStep(s?.logtimeMinHours));
        if (isAdmin) return; // ADMIN édite déjà tout, flags inutiles
        setSelfScheduleEnabled(Boolean(s?.selfScheduleEnabled));
        setSelfTimeOffEnabled(s?.selfTimeOffRequestEnabled !== false);
      })
      .catch(() => {
        if (cancelled) return;
        setSelfScheduleEnabled(false);
        setSelfTimeOffEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // Charge les affaires actives pour les modales create/edit. Nécessaire pour
  // l'ADMIN (toujours) et pour les non-ADMIN dès que l'édition self-service est
  // ouverte (le picker d'affaire en a besoin).
  //
  // Sprint 16 — en self-service (non-ADMIN), on restreint le picker aux affaires
  // de la Person liée via `mineOnly` (Sprint 7.2 : membre d'équipe actif OR
  // project manager OR a déjà loggué du temps). ADMIN bypass côté backend → voit
  // tout. Évite qu'un USER planifie sur des projets qui ne le concernent pas et
  // limite la fuite d'information sur le catalogue projets.
  useEffect(() => {
    if (!isAdmin && !selfScheduleEnabled) return undefined;
    let cancelled = false;
    apiAffaires
      .list('active', null, { mineOnly: !isAdmin })
      .then((data) => {
        if (cancelled) return;
        setAffaires(data?.items ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setAffairesError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, selfScheduleEnabled]);

  // Édition d'une Person depuis la colonne de gauche (clic sur le nom — ADMIN).
  const [editPersonId, setEditPersonId] = useState(null);

  // Ticket #91 — /api/schedule chargé par segments : un glissement de fenêtre
  // ne demande que les jours nouveaux, la grille reste affichée pendant l'appel.
  // Seul un changement de filtre affiche « Chargement… » ; `reloadKey`
  // (mutation, SSE — Sprint 17) recharge la fenêtre en silence.
  const [reloadKey, setReloadKey] = useState(0);
  const { conditions: filterConditions } = useFilterStore('schedule');
  const filterQuery = useMemo(() => toFilterQuery(filterConditions), [filterConditions]);
  // Ticket #77 — périmètre « Tout le monde » / « Mon équipe » (sa Person + ses
  // subordonnés directs), comme la saisie des temps. Réservé MANAGER / ADMIN ;
  // le backend borne `scope` par le rôle.
  const canScopeTeam = isAdmin || user?.role === 'MANAGER';
  const [personScope, setPersonScope] = useState('all');
  const effectiveScope = canScopeTeam ? personScope : 'all';
  // Un filtre autre que « Moi » force « Tout le monde » (à chaque changement
  // de filtre ; l'utilisateur peut ensuite rebasculer à la main).
  const nonSelfFilter = hasNonSelfFilter(filterConditions, user?.personId);
  useEffect(() => {
    if (nonSelfFilter) setPersonScope('all');
  }, [nonSelfFilter, filterQuery]);
  const {
    data: schedule,
    loading,
    fetching,
    error,
  } = useRangeData({
    period,
    depsKey: JSON.stringify({ scope: effectiveScope, filter: filterQuery }),
    reloadKey,
    fetchRange: ({ from, to, signal }) =>
      Promise.all([
        apiSchedule.get({
          from,
          to,
          withHolidays: true, // Sprint 5 — affichage grisé des jours fériés.
          filter: filterQuery,
          scope: effectiveScope,
          signal,
        }),
        // Ticket #6 — statuts de présence sur la même fenêtre (non bloquant).
        apiWorkLocations.list({ from, to, signal }).catch((e) => {
          if (signal.aborted) throw e;
          return { items: [] };
        }),
      ]).then(([data, wl]) => ({ holidaysByPerson: {}, ...data, workLocations: wl?.items ?? [] })),
    merge: mergeScheduleData,
    prune: pruneScheduleData,
    empty: EMPTY_SCHEDULE,
  });
  const workLocations = schedule.workLocations;

  // Index { personId → Map(iso → { morning, afternoon, full }) } pour le rendu.
  // `morning`/`afternoon` = { location, isRecurring, entryId, isFull } | null.
  // `full` = true si les deux demis viennent du même statut « journée ».
  const workLocByPerson = useMemo(() => {
    const m = new Map();
    for (const w of workLocations) {
      if (!m.has(w.personId)) m.set(w.personId, new Map());
      const dayMap = m.get(w.personId);
      const day = dayMap.get(w.date) ?? { morning: null, afternoon: null, full: false };
      day[w.slot] = {
        location: w.location,
        isRecurring: w.isRecurring,
        entryId: w.entryId,
        isFull: w.isFull,
      };
      dayMap.set(w.date, day);
    }
    // Marque `full` quand matin & aprem proviennent du même statut journée.
    for (const dayMap of m.values()) {
      for (const day of dayMap.values()) {
        day.full = Boolean(
          day.morning &&
            day.afternoon &&
            day.morning.isFull &&
            day.afternoon.isFull &&
            day.morning.entryId === day.afternoon.entryId,
        );
      }
    }
    return m;
  }, [workLocations]);

  // Sprint 17 — Temps réel via SSE. Le serveur pousse `schedule.change` à
  // chaque mutation d'allocation/congé ; on re-fetch la période courante (en
  // silence, ticket #91). Garde anti-geste : si un drag (create/move/resize)
  // est en cours, on diffère le reload jusqu'à la fin du geste pour ne pas
  // écraser l'état sous le doigt.
  const pendingReloadRef = useRef(false);
  const isDragging = Boolean(drag || createDraft || move || resize || vresize);
  useEffect(() => {
    busyRef.current = isDragging;
    if (!isDragging && pendingReloadRef.current) {
      pendingReloadRef.current = false;
      setReloadKey((k) => k + 1);
    }
  }, [isDragging]);

  useEffect(() => {
    const sub = apiSchedule.stream({
      onEvent: (e) => {
        if (e.event !== 'schedule.change') return;
        if (busyRef.current) {
          pendingReloadRef.current = true;
        } else {
          setReloadKey((k) => k + 1);
        }
      },
      // onError silencieux : openEventStream gère la reconnexion (backoff).
    });
    return () => sub.close();
  }, []);

  // Sprint 8.3 — tri front, locale `fr` (gère diacritiques), sensitivity=base
  // (insensible casse / accents). Champ primaire selon `personSort.field`,
  // champ secondaire = l'autre, pour stabiliser les egalités d'homonymes.
  const persons = useMemo(() => {
    const raw = schedule.persons ?? [];
    let filtered = raw;
    if (showActiveOnly) {
      // Lundi de la semaine courante à 00:00 (inclusif). Une endDate >= ce
      // lundi garde la personne visible (sortie en cours de semaine OK),
      // une endDate antérieure la masque. `parseISODate` renvoie déjà 00:00
      // local — on normalise le lundi pour comparer des bornes alignées.
      const monday = startOfWeek(new Date());
      monday.setHours(0, 0, 0, 0);
      const mondayMs = monday.getTime();
      filtered = raw.filter((p) => {
        if (!p.endDate) return true;
        const end = parseISODate(String(p.endDate).slice(0, 10));
        return end.getTime() >= mondayMs;
      });
    }
    const sorted = [...filtered];
    const opts = { sensitivity: 'base' };
    // #19 — ordre manuel prioritaire : on classe selon personOrder, les
    // personnes hors liste (nouvelles) tombant en fin, triées par nom.
    if (Array.isArray(personOrder) && personOrder.length) {
      const idx = new Map(personOrder.map((id, i) => [id, i]));
      sorted.sort((a, b) => {
        const ia = idx.has(a.id) ? idx.get(a.id) : Number.POSITIVE_INFINITY;
        const ib = idx.has(b.id) ? idx.get(b.id) : Number.POSITIVE_INFINITY;
        if (ia !== ib) return ia - ib;
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr', opts);
      });
      return sorted;
    }
    sorted.sort((a, b) => {
      const keyA = personSort.field === 'lastName'
        ? `${a.lastName} ${a.firstName}`
        : `${a.firstName} ${a.lastName}`;
      const keyB = personSort.field === 'lastName'
        ? `${b.lastName} ${b.firstName}`
        : `${b.firstName} ${b.lastName}`;
      const cmp = keyA.localeCompare(keyB, 'fr', opts);
      return personSort.dir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [schedule.persons, personSort, personOrder, showActiveOnly]);
  const totalRows = persons.length;

  // #19 — déplace `draggedId` à la position de `targetId` et fige l'ordre manuel.
  const reorderPerson = useCallback(
    (draggedId, targetId) => {
      if (!draggedId || draggedId === targetId) return;
      const base = persons.map((p) => p.id);
      const from = base.indexOf(draggedId);
      const to = base.indexOf(targetId);
      if (from < 0 || to < 0) return;
      base.splice(from, 1);
      base.splice(to, 0, draggedId);
      setPersonOrder(base);
    },
    [persons],
  );

  // Sprint 11 — Périmètre de suppression de congés pour un MANAGER : sa propre
  // Person + toute sa descendance (cohérent backend `canManagePerson`). Sert à
  // n'afficher le bouton « Supprimer » que sur les congés que le manager peut
  // réellement archiver (le backend reste l'autorité — UX uniquement).
  const isManager = user?.role === 'MANAGER';

  // Sprint 16 fix — `/api/schedule` n'expose PAS l'email des personnes, donc on
  // ne peut pas résoudre « ma Person » par correspondance d'email dans le
  // payload. On interroge l'endpoint dédié `/api/persons/me` (autorité serveur
  // par email lowercase, compatible impersonation).
  const [myPersonId, setMyPersonId] = useState(null);
  // Sous-traitant : peut déclarer ses indisponibilités sur sa propre ligne.
  const [iAmContractor, setIAmContractor] = useState(false);
  // Personne marquée à temps partiel : peut déclarer ses jours sur sa ligne.
  const [iAmPartTime, setIAmPartTime] = useState(false);
  useEffect(() => {
    if (isAdmin) {
      setMyPersonId(null); // ADMIN édite tout, pas besoin de résoudre sa Person
      setIAmContractor(false);
      setIAmPartTime(false);
      return undefined;
    }
    let cancelled = false;
    apiPersons
      .me()
      .then((p) => {
        if (cancelled) return;
        setMyPersonId(p?.id ?? null);
        setIAmContractor(p?.employeeType === 'CONTRACTOR');
        setIAmPartTime(Boolean(p?.partTime));
      })
      .catch(() => {
        if (cancelled) return;
        setMyPersonId(null); // pas de Person liée → pas d'édition
        setIAmContractor(false);
        setIAmPartTime(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.email]);

  const [manageablePersonIds, setManageablePersonIds] = useState(() => new Set());
  useEffect(() => {
    if (!isManager || !myPersonId) {
      setManageablePersonIds(new Set());
      return undefined;
    }
    let cancelled = false;
    apiPersonManagers
      .listReports(myPersonId)
      .then((data) => {
        if (cancelled) return;
        const ids = new Set((data?.items ?? []).map((r) => r.id).filter(Boolean));
        ids.add(myPersonId); // un manager peut aussi se gérer lui-même
        setManageablePersonIds(ids);
      })
      .catch(() => {
        if (!cancelled) setManageablePersonIds(new Set([myPersonId]));
      });
    return () => {
      cancelled = true;
    };
  }, [isManager, myPersonId]);

  // Sprint 16 — Person liée au compte courant (résolue par email, cohérent
  // backend). Sert au périmètre d'édition self-service d'un USER.
  // Sprint 16 — Périmètre d'édition du Schedule (UX uniquement ; le backend
  // `assertCanSchedule` reste l'autorité) :
  //  - ADMIN : tout.
  //  - sinon flag `selfScheduleEnabled` requis + VIEWER exclu (lecture seule),
  //    puis scope : USER = sa propre Person ; MANAGER = sa Person + descendance
  //    (manageablePersonIds, déjà chargé).
  const canScheduleForPerson = useCallback(
    (personId) => {
      if (isAdmin) return true;
      if (!selfScheduleEnabled || !personId) return false;
      if (user?.role === 'VIEWER') return false;
      if (isManager) return manageablePersonIds.has(personId);
      return personId === myPersonId; // USER
    },
    [isAdmin, selfScheduleEnabled, user?.role, isManager, manageablePersonIds, myPersonId],
  );

  // synotis-support#10 — personnes que l'utilisateur peut ajouter à une
  // création d'allocation multi-personnes (même règle que le glisser-créer).
  const schedulablePersons = useMemo(
    () => persons.filter((p) => canScheduleForPerson(p.id)),
    [persons, canScheduleForPerson],
  );

  // Peut-on éditer au moins une ligne ? (pilote le chargement des affaires, le
  // bandeau "lecture seule" et les curseurs de création.)
  const canScheduleAny =
    isAdmin ||
    (selfScheduleEnabled &&
      user?.role !== 'VIEWER' &&
      (isManager ? manageablePersonIds.size > 0 : Boolean(myPersonId)));

  // Un manager qui planifie un membre de son équipe doit voir les affaires de
  // CETTE personne, pas les siennes (`mineOnly` ne renvoie que celles du user
  // connecté). Même route que la saisie des temps (ticket #29) : `forPersonId`,
  // le backend revérifie `canManagePerson`. Cache par Person le temps de la vue.
  const [affairesByPerson, setAffairesByPerson] = useState({});
  const targetPersonId =
    detail?.type === 'allocation' ? detail.item?.personId : createDraft?.personId;
  const needsPersonAffaires =
    !isAdmin &&
    Boolean(targetPersonId) &&
    targetPersonId !== myPersonId &&
    canScheduleForPerson(targetPersonId);
  useEffect(() => {
    if (!needsPersonAffaires || affairesByPerson[targetPersonId]) return undefined;
    let cancelled = false;
    apiAffaires
      .list('active', null, { forPersonId: targetPersonId })
      .then((data) => {
        if (cancelled) return;
        setAffairesByPerson((prev) => ({ ...prev, [targetPersonId]: { items: data?.items ?? [] } }));
      })
      .catch((e) => {
        if (cancelled) return;
        setAffairesByPerson((prev) => ({ ...prev, [targetPersonId]: { items: [], error: e.message } }));
      });
    return () => {
      cancelled = true;
    };
  }, [needsPersonAffaires, targetPersonId, affairesByPerson]);
  const targetAffaires = needsPersonAffaires
    ? affairesByPerson[targetPersonId]?.items ?? []
    : affaires;
  const targetAffairesError = needsPersonAffaires
    ? affairesByPerson[targetPersonId]?.error ?? null
    : affairesError;

  // Indisponibilités : réservées aux lignes de sous-traitants. Le sous-traitant
  // les déclare sur SA ligne, même sans droit de planification ; un ADMIN les
  // saisit pour tout sous-traitant, un MANAGER pour ceux de son équipe. UX
  // uniquement — la route `POST /api/timeoffs` revérifie le type, la cible,
  // le statut de sous-traitant et le lien hiérarchique.
  const contractorIds = useMemo(
    () =>
      new Set(
        (schedule.persons ?? []).filter((p) => p.employeeType === 'CONTRACTOR').map((p) => p.id),
      ),
    [schedule.persons],
  );
  const canDeclareUnavailability = useCallback(
    (personId) => {
      if (!personId || user?.role === 'VIEWER') return false;
      if (isAdmin) return contractorIds.has(personId);
      if (isManager && personId !== myPersonId && manageablePersonIds.has(personId)) {
        return contractorIds.has(personId);
      }
      return iAmContractor && personId === myPersonId;
    },
    [isAdmin, isManager, iAmContractor, user?.role, myPersonId, manageablePersonIds, contractorIds],
  );

  // #86 — un temps partiel est supprimable par son auteur (validé d'office, on
  // le supprime pour le recréer ailleurs). UX uniquement — `DELETE
  // /api/timeoffs/:id` revérifie le type, la Person et le flag self-service.
  // La même condition ouvre la DÉCLARATION d'un temps partiel sur sa propre
  // ligne : un clic sur le jour, sans passer par « Mes congés ».
  const ownsPartTime = useCallback(
    (personId) =>
      !isAdmin &&
      selfTimeOffEnabled &&
      user?.role !== 'VIEWER' &&
      Boolean(personId) &&
      personId === myPersonId,
    [isAdmin, selfTimeOffEnabled, user?.role, myPersonId],
  );

  // Déclarer un temps partiel sur sa ligne : réservé aux personnes marquées
  // « temps partiel » sur leur fiche. La suppression (`ownsPartTime`) reste
  // ouverte à tout auteur, pour qu'un temps partiel posé avant le drapeau ne
  // devienne pas indélébile.
  const canDeclarePartTime = useCallback(
    (personId) => iAmPartTime && ownsPartTime(personId),
    [iAmPartTime, ownsPartTime],
  );

  // Plage de jours RENDUE : jours visibles + BUFFER_DAYS, arrondie par blocs
  // de RENDER_CHUNK_DAYS (ticket #91). Sans cet arrondi, la plage changeait à
  // chaque jour franchi — tous les 20 px en zoom Mois — et chaque ligne
  // (`PersonRow`, mémoïsée) était re-rendue à chaque image de défilement.
  const visibleWidth = Math.max(0, viewport.width - LEFT_COL_WIDTH_PX);
  const renderChunk = RENDER_CHUNK_DAYS[zoom];
  const firstVisibleDay = Math.max(
    0,
    Math.floor((Math.floor(scrollLeft / dayWidth) - BUFFER_DAYS) / renderChunk) * renderChunk,
  );
  const lastVisibleDay = Math.min(
    totalDays,
    Math.ceil((Math.ceil((scrollLeft + visibleWidth) / dayWidth) + BUFFER_DAYS) / renderChunk) *
      renderChunk,
  );
  const visibleHeight = Math.max(0, viewport.height - HEADER_HEIGHT_PX);

  // Ticket #50 — bornes réellement à l'écran (sans BUFFER_DAYS), pour le
  // libellé de période de la toolbar.
  const visibleFromIdx = Math.max(0, Math.min(totalDays - 1, Math.floor(scrollLeft / dayWidth)));
  const visibleToIdx = Math.max(
    visibleFromIdx,
    Math.min(totalDays - 1, Math.ceil((scrollLeft + visibleWidth) / dayWidth) - 1),
  );
  const visibleFrom = addDays(period.from, visibleFromIdx);
  const visibleTo = addDays(period.from, visibleToIdx);

  // Pré-indexe allocations / timeoffs par personId pour éviter un O(N×M) au render.
  // Ticket #91 — listes par personne STABLES : une personne dont les items
  // n'ont pas changé garde le même tableau, donc sa ligne mémoïsée et ses voies
  // en cache (cf. laneDataByPerson) ne sont pas recalculées.
  const prevAllocByPersonRef = useRef(new Map());
  const allocByPerson = useMemo(() => {
    const m = new Map();
    for (const a of schedule.allocations ?? []) {
      if (!m.has(a.personId)) m.set(a.personId, []);
      m.get(a.personId).push(a);
    }
    const stable = reuseUnchangedLists(m, prevAllocByPersonRef.current);
    prevAllocByPersonRef.current = stable;
    return stable;
  }, [schedule.allocations]);

  const prevTimeOffsByPersonRef = useRef(new Map());
  const timeOffsByPerson = useMemo(() => {
    const m = new Map();
    for (const t of schedule.timeOffs ?? []) {
      for (const pid of t.personIds ?? []) {
        if (!m.has(pid)) m.set(pid, []);
        m.get(pid).push(t);
      }
    }
    const stable = reuseUnchangedLists(m, prevTimeOffsByPersonRef.current);
    prevTimeOffsByPersonRef.current = stable;
    return stable;
  }, [schedule.timeOffs]);

  // Sprint 5 — Index Map<personId, Map<isoDate, holiday>> pour O(1) au render.
  const holidaysByPersonIndex = useMemo(() => {
    const m = new Map();
    const src = schedule.holidaysByPerson ?? {};
    for (const [pid, list] of Object.entries(src)) {
      const inner = new Map();
      for (const h of list) {
        const iso = (h.date ?? '').slice(0, 10);
        if (iso) inner.set(iso, h);
      }
      m.set(pid, inner);
    }
    return m;
  }, [schedule.holidaysByPerson]);

  // Sprint 24 — contexte pour la détection de surbooking (front, non bloquant).
  const overbookCtx = useMemo(
    () => ({
      allocations: schedule.allocations ?? [],
      timeOffsByPerson,
      holidaysByPersonIndex,
    }),
    [schedule.allocations, timeOffsByPerson, holidaysByPersonIndex],
  );

  // ---------- Voies (lanes) + hauteurs de ligne variables ----------
  // Pour chaque personne, on calcule le nombre de voies (chevauchements
  // simultanés allocations + congés). La hauteur de sa ligne = laneHeight ×
  // laneCount → les items qui se chevauchent restent tous visibles.
  // Ticket #91 — voies en cache par personne : recalculées seulement si ses
  // allocations, ses congés ou sa capacité ont changé.
  const laneCacheRef = useRef(new Map());
  const laneDataByPerson = useMemo(() => {
    const m = new Map();
    const cache = laneCacheRef.current;
    const nextCache = new Map();
    for (const p of persons) {
      const allocs = allocByPerson.get(p.id) ?? EMPTY_LIST;
      const tos = timeOffsByPerson.get(p.id) ?? EMPTY_LIST;
      const cap = computeDailyCapacity(p.workDaysHours);
      const hit = cache.get(p.id);
      const lanes =
        hit && hit.allocs === allocs && hit.tos === tos && hit.cap === cap
          ? hit.lanes
          : computeRowLanes(allocs, tos, cap);
      nextCache.set(p.id, { allocs, tos, cap, lanes });
      m.set(p.id, lanes);
    }
    laneCacheRef.current = nextCache;
    return m;
  }, [persons, allocByPerson, timeOffsByPerson]);

  // Hauteurs de ligne + offsets cumulés (prefix sum) pour la virtualisation à
  // hauteurs variables.
  const rowHeights = useMemo(
    () =>
      persons.map(
        (p) =>
          laneHeight * (laneDataByPerson.get(p.id)?.totalRatio ?? 1) +
          // Marge verticale de la ligne : un inset en haut + un espace en bas.
          // Les blocs d'un même jour sont collés entre eux (pas de trou entre
          // les 2 squares de 4h) ; tout l'espace libre est rejeté sous la pile.
          BLOCK_VERTICAL_PAD * 2 +
          // Bandeau de statut réservé en haut de ligne (cf. WL_BAND_HEIGHT).
          WL_BAND_HEIGHT,
      ),
    [persons, laneDataByPerson, laneHeight],
  );
  const rowTops = useMemo(() => {
    const tops = new Array(rowHeights.length + 1);
    let acc = 0;
    for (let i = 0; i < rowHeights.length; i++) {
      tops[i] = acc;
      acc += rowHeights[i];
    }
    tops[rowHeights.length] = acc;
    return tops;
  }, [rowHeights]);
  const totalGridHeight = rowTops[rowTops.length - 1] ?? 0;

  // Recherche binaire : plus grand index i tel que rowTops[i] <= y.
  const rowIndexAtOffset = (y) => {
    let lo = 0;
    let hi = rowHeights.length - 1;
    let res = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (rowTops[mid] <= y) {
        res = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return res;
  };
  const firstVisibleRow = Math.max(0, rowIndexAtOffset(scrollTop) - BUFFER_ROWS);
  const lastVisibleRow = Math.min(
    totalRows,
    rowIndexAtOffset(scrollTop + visibleHeight) + 1 + BUFFER_ROWS,
  );

  // ---------- Navigation ----------
  // Changement de zoom, flèches et clavier ← / → : gérés par
  // `useSlidingTimeline` (ticket #91). Les flèches font défiler la vue (#9) ;
  // arrivé au bord, la fenêtre glisse et le défilement se poursuit sans limite.
  const shift = navigate;
  const gotoToday = () => {
    const cfg = PERIOD_BY_ZOOM[zoom];
    // Ancre = premier jour visible souhaité. En zoom Mois on cale sur le début
    // du mois pour garder des cartouches de mois alignés.
    const anchor =
      zoom === 'month'
        ? startOfMonth(addDays(TODAY, -cfg.backDays))
        : addDays(startOfWeek(TODAY), -cfg.backDays);
    anchorTo(anchor);
  };

  // Ticket #91 — callbacks à identité stable pour `PersonRow` (mémoïsée) :
  // recréés inline à chaque rendu, ils re-rendaient toutes les lignes à chaque
  // image de scroll.
  const rowHandlers = {
    onCellPointerDown: useStableHandler((...args) => onCellPointerDown(...args)),
    onCellPointerEnter: useStableHandler((...args) => onCellPointerEnter(...args)),
    onAllocationPointerDown: useStableHandler((...args) => onAllocationPointerDown(...args)),
    onResizeHandlePointerDown: useStableHandler((...args) => onResizeHandlePointerDown(...args)),
    onVresizeHandlePointerDown: useStableHandler((...args) => onVresizeHandlePointerDown(...args)),
    onTimeOffVresizePointerDown: useStableHandler((...args) => onTimeOffVresizePointerDown(...args)),
    onSelectAllocation: useStableHandler((a, person) =>
      isDeleteMode
        ? deleteAllocationOnClick(a)
        : setDetail({ type: 'allocation', item: a, person }),
    ),
    onSelectTimeOff: useStableHandler((t, person) =>
      isDeleteMode
        ? setToast({ message: 'Les congés sont gérés dans Gescom', kind: 'info' })
        : setDetail({ type: 'timeoff', item: t, person }),
    ),
    onSelectPerson: useStableHandler((person) => setEditPersonId(person.id)),
    onReorderPerson: useStableHandler((...args) => reorderPerson(...args)),
    onStatusClick: useStableHandler((personId, iso, half, current) =>
      setStatusModal({ personId, iso, half, current }),
    ),
  };

  // Index aujourd'hui (pour styling `data-is-today`).
  const todayIndex = diffDays(TODAY, period.from);

  // Le drag-to-create est désactivé en zoom mois (trop imprécis pour saisir un
  // jour exact). Le drag-to-move/resize reste actif (snap au jour via round).
  // Sprint 16 — gardes "grossières" (zoom + au moins une ligne éditable). Le
  // gating fin par personne est appliqué dans les handlers et par ligne via
  // `canScheduleForPerson` (et reste validé côté backend).
  const canCreate = canScheduleAny && zoom !== 'month' && !isDeleteMode;
  // Drag-to-move/resize : activable dès qu'on peut éditer au moins une ligne.
  const canEditAllocations = canScheduleAny;

  // ---------- Ticket #62 — mode Suppression ----------
  const openToolMenu = useCallback(
    (e) => {
      // Sans droit de planification, le mode Suppression n'a pas d'objet :
      // on laisse le menu natif du navigateur.
      if (!canScheduleAny) return;
      e.preventDefault();
      setToolMenu({ x: e.clientX, y: e.clientY });
    },
    [canScheduleAny],
  );

  const performDeleteAllocation = useCallback(async (alloc, scope) => {
    try {
      if (!scope || scope === 'all_occurrences') {
        await apiAllocations.archive(alloc.id);
      } else {
        await apiAllocations.deleteInstance(
          alloc.id,
          alloc.occurrenceDate ?? alloc.startDate.slice(0, 10),
          scope,
        );
      }
      setToast({ message: 'Allocation supprimée', kind: 'success' });
    } catch (err) {
      setToast({ message: `Suppression impossible : ${err.message}`, kind: 'error' });
    } finally {
      setReloadKey((k) => k + 1);
    }
  }, []);

  const deleteAllocationOnClick = useCallback(
    (alloc) => {
      if (!canScheduleForPerson(alloc.personId)) {
        setToast({ message: 'Droits insuffisants pour supprimer cette allocation', kind: 'error' });
        return;
      }
      // Série récurrente : on demande le périmètre, jamais de suppression
      // silencieuse de toute la série.
      if (alloc.isRecurring) {
        setPendingDelete(alloc);
        return;
      }
      performDeleteAllocation(alloc, null);
    },
    [canScheduleForPerson, performDeleteAllocation],
  );

  // ---------- Helper : convertir un clientX en index de jour ----------
  // Utilisé par le drag-to-move/resize (qui écoutent pointermove global).
  // On lit la position du container et on calcule l'offset relatif.
  const clientXToDayIndex = useCallback(
    (clientX) => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const offsetX = clientX - rect.left - LEFT_COL_WIDTH_PX + el.scrollLeft;
      if (offsetX < 0) return 0;
      const idx = Math.floor(offsetX / dayWidth);
      return Math.max(0, Math.min(totalDays - 1, idx));
    },
    [dayWidth, totalDays],
  );

  // ---------- Drag-to-create handlers ----------
  // IMPORTANT : pas de setPointerCapture ici, contrairement à drag-to-move.
  // setPointerCapture route TOUS les events pointer vers l'élément capturé
  // → les autres day-cells ne reçoivent jamais leur onPointerEnter → le
  // drag-to-select ne s'étend pas. Pattern documenté analyse/
  // float-drag-selection-architecture.md §3 : on s'appuie sur le bubbling
  // natif (onPointerEnter sur chaque cellule).
  const onCellPointerDown = useCallback(
    (personId, dayIndex, _e) => {
      const canAllocate = canCreate && canScheduleForPerson(personId);
      const selfAbsence =
        zoom !== 'month' &&
        !isDeleteMode &&
        (canDeclareUnavailability(personId) || canDeclarePartTime(personId));
      if (!canAllocate && !selfAbsence) return;
      setDrag({ personId, anchorDayIndex: dayIndex, endDayIndex: dayIndex });
    },
    [canCreate, canScheduleForPerson, canDeclareUnavailability, canDeclarePartTime, zoom, isDeleteMode],
  );

  const onCellPointerEnter = useCallback((personId, dayIndex) => {
    setDrag((d) => {
      if (!d) return d;
      if (d.personId !== personId) return d;
      if (d.endDayIndex === dayIndex) return d;
      return { ...d, endDayIndex: dayIndex };
    });
  }, []);

  // pointerup global → commit selon le type de drag actif.
  useEffect(() => {
    if (!drag && !move && !resize && !vresize) return undefined;
    const onUp = () => {
      if (drag) {
        const start = Math.min(drag.anchorDayIndex, drag.endDayIndex);
        const end = Math.max(drag.anchorDayIndex, drag.endDayIndex);
        setCreateDraft({ personId: drag.personId, startDayIndex: start, endDayIndex: end });
        setDrag(null);
      }
      if (move) {
        commitMove();
      }
      if (resize) {
        commitResize();
      }
      if (vresize) {
        commitVresize();
      }
    };
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, move, resize, vresize]);

  // Échap annule un drag/move/resize OU ferme la modale de création.
  useEffect(() => {
    if (!drag && !createDraft && !move && !resize && !vresize) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (drag) setDrag(null);
      if (createDraft) setCreateDraft(null);
      if (move) setMove(null);
      if (resize) setResize(null);
      if (vresize) setVresize(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag, createDraft, move, resize, vresize]);

  // Auto-scroll au bord pendant n'importe quel drag (analyse §A).
  useEffect(() => {
    if (!drag && !move && !resize) return undefined;
    let rafId = null;
    let mouseX = 0;
    const onMove = (e) => {
      mouseX = e.clientX;
    };
    const tick = () => {
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const leftEdge = rect.left + LEFT_COL_WIDTH_PX;
        const rightEdge = rect.right;
        if (mouseX > leftEdge && mouseX < leftEdge + AUTOSCROLL_EDGE_PX) {
          el.scrollLeft = Math.max(0, el.scrollLeft - AUTOSCROLL_SPEED_PX);
        } else if (mouseX < rightEdge && mouseX > rightEdge - AUTOSCROLL_EDGE_PX) {
          el.scrollLeft += AUTOSCROLL_SPEED_PX;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    window.addEventListener('pointermove', onMove);
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [drag, move, resize]);

  // ---------- Drag-to-move handlers ----------
  const onAllocationPointerDown = useCallback(
    (allocation, e) => {
      if (!canEditAllocations || !canScheduleForPerson(allocation.personId)) return;
      // Anti-conflit avec drag-to-create : ne pas démarrer un drag sur cellule.
      e.stopPropagation();
      if (e.button !== undefined && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Ignored.
      }
      const startIdx = clientXToDayIndex(e.clientX);
      if (startIdx === null) return;
      setMove({
        allocationId: allocation.id,
        // Identité de l'occurrence cliquée : indispensable pour les séries
        // récurrentes où plusieurs instances partagent le même `id`.
        instanceId: allocation.instanceId,
        dragStartDayIdx: startIdx,
        deltaDay: 0,
      });
    },
    [canEditAllocations, canScheduleForPerson, clientXToDayIndex],
  );

  // pointermove global pendant un move → met à jour deltaDay.
  useEffect(() => {
    if (!move) return undefined;
    const onPointerMove = (e) => {
      const idx = clientXToDayIndex(e.clientX);
      if (idx === null) return;
      const delta = idx - move.dragStartDayIdx;
      setMove((m) => (m && m.deltaDay !== delta ? { ...m, deltaDay: delta } : m));
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [move, clientXToDayIndex]);

  // Commit du move (extrait pour pouvoir l'appeler depuis pointerup global).
  // Stocké dans une ref pour éviter le piège des closures stale.
  const movingAllocationRef = useRef(null);
  useEffect(() => {
    movingAllocationRef.current = move;
  }, [move]);

  const commitMove = useCallback(() => {
    const m = movingAllocationRef.current;
    if (!m) return;
    setMove(null);
    // Retrouve l'instance EXACTE (par instanceId) : pour une série récurrente,
    // plusieurs instances partagent `id` — chercher par `id` renverrait
    // toujours la première occurrence (bug suppression de la mauvaise date).
    const findInstance = () =>
      (schedule.allocations ?? []).find((a) =>
        m.instanceId ? a.instanceId === m.instanceId : a.id === m.allocationId,
      );
    if (!m.deltaDay) {
      // Pas de déplacement → c'est un clic, on ouvre l'édition.
      const alloc = findInstance();
      const person = persons.find((p) => p.id === alloc?.personId);
      if (alloc) setDetail({ type: 'allocation', item: alloc, person });
      return;
    }
    const alloc = findInstance();
    if (!alloc) return;
    const newStart = addDays(parseISODate(alloc.startDate), m.deltaDay);
    const newEnd = addDays(parseISODate(alloc.endDate), m.deltaDay);
    const startIso = toISODate(newStart);
    const endIso = toISODate(newEnd);
    const doUpdate = () => {
      apiAllocations
        .update(alloc.id, { startDate: startIso, endDate: endIso })
        .then(() => {
          setReloadKey((k) => k + 1);
        })
        .catch((err) => {
          setToast({ message: `Déplacement impossible : ${err.message}`, kind: 'error' });
          setReloadKey((k) => k + 1);
        });
    };
    // Sprint 24 — surbooking : confirmation explicite (non bloquante backend).
    const days = computeOverbookedDays({
      personId: alloc.personId,
      startIso,
      endIso,
      newHoursPerDay: alloc.hoursPerDay,
      ctx: overbookCtx,
      excludeId: alloc.id,
    });
    if (days.length > 0) {
      const person = persons.find((p) => p.id === alloc.personId);
      setPendingOverbook({
        days,
        personName: person ? `${person.firstName} ${person.lastName}`.trim() : 'Cette personne',
        onConfirm: doUpdate,
      });
    } else {
      doUpdate();
    }
  }, [schedule.allocations, persons, overbookCtx]);

  // ---------- Drag-to-resize handlers ----------
  const onResizeHandlePointerDown = useCallback(
    (allocation, edge, e) => {
      if (!canEditAllocations || !canScheduleForPerson(allocation.personId)) return;
      e.stopPropagation();
      if (e.button !== undefined && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Ignored.
      }
      const startIdx = diffDays(parseISODate(allocation.startDate), period.from);
      const endIdx = diffDays(parseISODate(allocation.endDate), period.from);
      setResize({
        allocationId: allocation.id,
        instanceId: allocation.instanceId,
        edge,
        originalStartIdx: startIdx,
        originalEndIdx: endIdx,
        currentStartIdx: startIdx,
        currentEndIdx: endIdx,
      });
    },
    [canEditAllocations, canScheduleForPerson, period.from],
  );

  useEffect(() => {
    if (!resize) return undefined;
    const onPointerMove = (e) => {
      const idx = clientXToDayIndex(e.clientX);
      if (idx === null) return;
      setResize((r) => {
        if (!r) return r;
        if (r.edge === 'left') {
          const next = Math.min(idx, r.originalEndIdx);
          if (next === r.currentStartIdx) return r;
          return { ...r, currentStartIdx: next };
        }
        // edge === 'right'
        const next = Math.max(idx, r.originalStartIdx);
        if (next === r.currentEndIdx) return r;
        return { ...r, currentEndIdx: next };
      });
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [resize, clientXToDayIndex]);

  const resizingAllocationRef = useRef(null);
  useEffect(() => {
    resizingAllocationRef.current = resize;
  }, [resize]);

  const commitResize = useCallback(() => {
    const r = resizingAllocationRef.current;
    if (!r) return;
    setResize(null);
    const changed =
      r.currentStartIdx !== r.originalStartIdx || r.currentEndIdx !== r.originalEndIdx;
    if (!changed) return;
    const alloc = (schedule.allocations ?? []).find((a) =>
      r.instanceId ? a.instanceId === r.instanceId : a.id === r.allocationId,
    );
    if (!alloc) return;
    const patch = {};
    if (r.currentStartIdx !== r.originalStartIdx) {
      patch.startDate = toISODate(addDays(period.from, r.currentStartIdx));
    }
    if (r.currentEndIdx !== r.originalEndIdx) {
      patch.endDate = toISODate(addDays(period.from, r.currentEndIdx));
    }
    const doUpdate = () => {
      apiAllocations
        .update(alloc.id, patch)
        .then(() => {
          setReloadKey((k) => k + 1);
        })
        .catch((err) => {
          setToast({ message: `Redimensionnement impossible : ${err.message}`, kind: 'error' });
          setReloadKey((k) => k + 1);
        });
    };
    // Sprint 24 — surbooking sur la plage résultante après resize.
    const startIso = toISODate(addDays(period.from, r.currentStartIdx));
    const endIso = toISODate(addDays(period.from, r.currentEndIdx));
    const days = computeOverbookedDays({
      personId: alloc.personId,
      startIso,
      endIso,
      newHoursPerDay: alloc.hoursPerDay,
      ctx: overbookCtx,
      excludeId: alloc.id,
    });
    if (days.length > 0) {
      const person = persons.find((p) => p.id === alloc.personId);
      setPendingOverbook({
        days,
        personName: person ? `${person.firstName} ${person.lastName}`.trim() : 'Cette personne',
        onConfirm: doUpdate,
      });
    } else {
      doUpdate();
    }
  }, [schedule.allocations, period.from, persons, overbookCtx]);

  // ---------- Ticket #48 — Drag-to-resize vertical (heures/jour) ----------
  // Le bloc est ancré en haut de sa voie et sa hauteur vaut
  // `hoursPerDay / dailyCapacity * laneHeight`. Tirer la poignée basse de
  // `dy` pixels équivaut donc à `dy / laneHeight * dailyCapacity` heures,
  // arrondies au pas `logtimeMinHours` (paramètre admin, défaut 1h).
  //  - allocation : bornée à [pas, 12h] comme les modales (on peut dépasser
  //    la capacité du jour : le surbooking demande confirmation) ;
  //  - congé (temps partiel / indisponibilité) : bornée à [pas, capacité du
  //    jour] — atteindre la capacité repasse le congé en journée entière.
  const onVresizeHandlePointerDown = useCallback(
    (allocation, e, { laneHeight, dailyCapacity }) => {
      if (!canEditAllocations || !canScheduleForPerson(allocation.personId)) return;
      e.stopPropagation();
      if (e.button !== undefined && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Ignored.
      }
      const hours = Number(allocation.hoursPerDay) || 0;
      if (!hours) return;
      setVresize({
        kind: 'allocation',
        allocationId: allocation.id,
        instanceId: allocation.instanceId,
        personId: allocation.personId,
        originalHours: hours,
        currentHours: hours,
        startClientY: e.clientY,
        laneHeight: laneHeight || ROW_HEIGHT_PX,
        dailyCapacity: dailyCapacity || FULL_DAY_HOURS,
        step: hourStep,
        maxHours: MAX_HOURS_PER_DAY,
      });
    },
    [canEditAllocations, canScheduleForPerson, hourStep],
  );

  // Ticket #48 — qui peut ajuster les heures d'un congé depuis le planning ?
  // Uniquement les types gérés dans Synolog (les absences Gescom, partielles
  // comprises, sont maîtrisées par Gescom), et seulement leur auteur — mêmes
  // conditions que la déclaration — ou un ADMIN. Les demi-journées ne valent
  // qu'en journée entière : un congé borné à la demi-journée n'a pas de
  // poignée. UX uniquement : `PATCH /api/timeoffs` revérifie tout.
  const canResizeTimeOff = useCallback(
    (timeOff, personId) => {
      const typeName = timeOff?.timeOffType?.name;
      if (!HOURS_RESIZABLE_TIMEOFF_TYPES.has(typeName)) return false;
      if (timeOff.fullDay && ((timeOff.startHalf ?? 'AM') !== 'AM' || (timeOff.endHalf ?? 'PM') !== 'PM')) {
        return false;
      }
      if (isAdmin) return true;
      return typeName === UNAVAILABILITY_TYPE_NAME
        ? canDeclareUnavailability(personId)
        : canDeclarePartTime(personId);
    },
    [isAdmin, canDeclareUnavailability, canDeclarePartTime],
  );

  const onTimeOffVresizePointerDown = useCallback(
    (timeOff, e, { laneHeight, dailyCapacity, personId }) => {
      if (!canResizeTimeOff(timeOff, personId)) return;
      e.stopPropagation();
      if (e.button !== undefined && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Ignored.
      }
      const capacity = dailyCapacity || FULL_DAY_HOURS;
      const hours = timeOff.fullDay ? capacity : Number(timeOff.hoursPerDay) || 0;
      if (!hours) return;
      const original = Math.min(hours, capacity);
      setVresize({
        kind: 'timeoff',
        timeOffId: timeOff.id,
        instanceId: timeOff.instanceId,
        personId,
        originalHours: original,
        currentHours: original,
        startClientY: e.clientY,
        laneHeight: laneHeight || ROW_HEIGHT_PX,
        dailyCapacity: capacity,
        step: hourStep,
        maxHours: capacity,
      });
    },
    [canResizeTimeOff, hourStep],
  );

  useEffect(() => {
    if (!vresize) return undefined;
    const onPointerMove = (e) => {
      setVresize((v) => {
        if (!v) return v;
        const dy = e.clientY - v.startClientY;
        // Sans vrai geste, on garde la valeur d'origine : une valeur hors du
        // pas (ex. 7,5h avec un pas de 1h) serait sinon arrondie au simple clic.
        const next =
          Math.abs(dy) < 3
            ? v.originalHours
            : snapHours(v.originalHours + (dy / v.laneHeight) * v.dailyCapacity, v.step, v.maxHours);
        if (next === v.currentHours) return v;
        return { ...v, currentHours: next };
      });
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [vresize]);

  const vresizingAllocationRef = useRef(null);
  useEffect(() => {
    vresizingAllocationRef.current = vresize;
  }, [vresize]);

  // Ticket #48 — congé récurrent : on demande le périmètre (cette occurrence,
  // les suivantes, toute la série) avant d'appliquer les nouvelles heures.
  // pendingTimeOffHours = null OU { timeOff, data }
  const [pendingTimeOffHours, setPendingTimeOffHours] = useState(null);

  const saveTimeOffHours = useCallback((timeOff, data, scope) => {
    const request = scope
      ? apiTimeOffs.patchInstance(
          timeOff.id,
          timeOff.occurrenceDate ?? timeOff.startDate.slice(0, 10),
          scope,
          data,
        )
      : apiTimeOffs.update(timeOff.id, data);
    request
      .then(() => setReloadKey((k) => k + 1))
      .catch((err) => {
        setToast({ message: `Modification des heures impossible : ${err.message}`, kind: 'error' });
        setReloadKey((k) => k + 1);
      });
  }, []);

  const commitTimeOffVresize = useCallback(
    (v) => {
      const timeOff = (schedule.timeOffs ?? []).find((t) =>
        v.instanceId ? t.instanceId === v.instanceId : t.id === v.timeOffId,
      );
      if (!timeOff) return;
      // Atteindre la capacité du jour = journée entière (hoursPerDay 0, comme
      // à la création) ; en deçà, une durée en heures.
      const data =
        v.currentHours >= v.dailyCapacity
          ? { fullDay: true, hoursPerDay: 0 }
          : { fullDay: false, hoursPerDay: v.currentHours };
      if (timeOff.isRecurring) {
        setPendingTimeOffHours({ timeOff, data });
        return;
      }
      saveTimeOffHours(timeOff, data, null);
    },
    [schedule.timeOffs, saveTimeOffHours],
  );

  const commitVresize = useCallback(() => {
    const v = vresizingAllocationRef.current;
    if (!v) return;
    setVresize(null);
    if (v.currentHours === v.originalHours) return;
    if (v.kind === 'timeoff') {
      commitTimeOffVresize(v);
      return;
    }
    const alloc = (schedule.allocations ?? []).find((a) =>
      v.instanceId ? a.instanceId === v.instanceId : a.id === v.allocationId,
    );
    if (!alloc) return;
    const doUpdate = () => {
      apiAllocations
        .update(alloc.id, { hoursPerDay: v.currentHours })
        .then(() => {
          setReloadKey((k) => k + 1);
        })
        .catch((err) => {
          setToast({ message: `Modification des heures impossible : ${err.message}`, kind: 'error' });
          setReloadKey((k) => k + 1);
        });
    };
    // Sprint 24 — surbooking : même confirmation que move/resize horizontal.
    const days = computeOverbookedDays({
      personId: alloc.personId,
      startIso: alloc.startDate.slice(0, 10),
      endIso: alloc.endDate.slice(0, 10),
      newHoursPerDay: v.currentHours,
      ctx: overbookCtx,
      excludeId: alloc.id,
    });
    if (days.length > 0) {
      const person = persons.find((p) => p.id === alloc.personId);
      setPendingOverbook({
        days,
        personName: person ? `${person.firstName} ${person.lastName}`.trim() : 'Cette personne',
        onConfirm: doUpdate,
      });
    } else {
      doUpdate();
    }
  }, [schedule.allocations, persons, overbookCtx, commitTimeOffVresize]);

  // Geometry actuelle de la sélection (pour overlay).
  const dragGeom = useMemo(() => {
    if (!drag) return null;
    const start = Math.min(drag.anchorDayIndex, drag.endDayIndex);
    const end = Math.max(drag.anchorDayIndex, drag.endDayIndex);
    return {
      left: start * dayWidth,
      width: (end - start + 1) * dayWidth,
    };
  }, [drag, dayWidth]);

  // ---------- Header jours (zoom day/week) ou mois (zoom month) ----------
  // En zoom mois on remplace la grille de jours par une grille de mois pour
  // un header lisible. Le calcul reste basé sur des indices de jour
  // (cohérent avec allocations / drag).
  // Ticket #50 — la bande mois est désormais affichée à TOUS les zooms : sans
  // elle, en zoom jour/semaine, on ne voit que des numéros de jour et on ne
  // sait pas sur quel mois on se trouve.
  const monthGroups = useMemo(() => {
    const groups = [];
    let cursor = startOfMonth(period.from);
    while (cursor <= period.to) {
      const monthStart = cursor;
      const nextMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
      const startIdx = Math.max(0, diffDays(monthStart, period.from));
      const endIdx = Math.min(totalDays - 1, diffDays(addDays(nextMonth, -1), period.from));
      groups.push({
        label: `${MONTHS_FR_LONG[monthStart.getMonth()]} ${monthStart.getFullYear()}`,
        // Repli quand la colonne du mois est trop étroite pour le libellé long.
        shortLabel: `${MONTHS_FR[monthStart.getMonth()]} ${String(monthStart.getFullYear()).slice(2)}`,
        left: startIdx * dayWidth,
        width: (endIdx - startIdx + 1) * dayWidth,
        startIdx,
        endIdx,
      });
      cursor = nextMonth;
    }
    return groups;
  }, [period.from, period.to, totalDays, dayWidth]);

  // Découpage en semaines visibles (ticket #10) : en vue mois, on affiche un
  // bandeau « S{n} · {lundi} » sous les mois pour situer la semaine/le jour.
  // Ticket #50 : calculé à tous les zooms, car en zoom jour/semaine ce sont
  // ces groupes qui portent le libellé du mois (un mois y est bien plus large
  // que la viewport, un seul libellé par mois sortait de l'écran au scroll).
  const weekGroups = useMemo(() => {
    const groups = [];
    let cursor = startOfWeek(period.from);
    while (cursor <= period.to) {
      const weekEnd = addDays(cursor, 6);
      const startIdx = Math.max(0, diffDays(cursor, period.from));
      const endIdx = Math.min(totalDays - 1, diffDays(weekEnd, period.from));
      // Bornes clampées à la fenêtre : le libellé de mois doit décrire les
      // jours réellement présents, pas la semaine calendaire théorique.
      const spanFrom = addDays(period.from, startIdx);
      const spanTo = addDays(period.from, endIdx);
      groups.push({
        label: `S${isoWeekNum(cursor)} · ${cursor.getDate()}/${cursor.getMonth() + 1}`,
        monthLabel: fmtMonthSpan(spanFrom, spanTo),
        monthLabelShort: fmtMonthSpan(spanFrom, spanTo, true),
        left: startIdx * dayWidth,
        width: (endIdx - startIdx + 1) * dayWidth,
        startIdx,
      });
      cursor = addDays(cursor, 7);
    }
    return groups;
  }, [period.from, period.to, totalDays, dayWidth]);

  // ---------- Render ----------
  // Ticket #91 — son résultat pilote l'affichage des poignées : vraies dépendances.
  const canResizeTimeOffInRow = useCallback(
    (t, personId) => !isDeleteMode && canResizeTimeOff(t, personId),
    [isDeleteMode, canResizeTimeOff],
  );

  return (
    <div
      data-is-any-row-dragging={drag || move || resize ? 'true' : 'false'}
      data-is-any-row-vresizing={vresize ? 'true' : 'false'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--bg)',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 24px',
          borderBottom: '1px solid var(--glass-border)',
          background: 'var(--card-bg)',
          flexWrap: 'wrap',
        }}
      >
        <CalendarDays size={20} color="var(--accent)" />
        <h1 style={{ margin: 0, fontSize: 18, color: 'var(--text)' }}>Planning</h1>

        <div style={{ display: 'inline-flex', gap: 4, marginLeft: 16 }}>
          {['day', 'week', 'month'].map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZoom(z)}
              style={{
                background: zoom === z ? 'var(--accent)' : 'transparent',
                color: zoom === z ? '#fff' : 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-pill)',
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {z === 'day' ? 'Jour' : z === 'week' ? 'Semaine' : 'Mois'}
            </button>
          ))}
        </div>

        {/* Sprint 12 — densité d'affichage (hauteur des lignes) */}
        <DensityPicker value={density} onChange={changeDensity} />

        {/* Ticket #62 — mode courant, changeable par clic droit sur la grille */}
        {canScheduleAny ? <ToolModeBadge modes={TOOL_MODES} mode={toolMode} /> : null}

        <button
          type="button"
          onClick={() => setShowActiveOnly((v) => !v)}
          aria-pressed={showActiveOnly}
          title={
            showActiveOnly
              ? 'Masque les personnes sorties avant le lundi de la semaine courante. Cliquer pour tout afficher.'
              : 'Affiche toutes les personnes (y compris celles déjà sorties). Cliquer pour ne garder que les actifs.'
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 12,
            padding: '4px 12px',
            background: showActiveOnly ? 'var(--accent)' : 'transparent',
            color: showActiveOnly ? '#fff' : 'var(--text)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <UserCheck size={14} />
          Actifs seulement
        </button>

        {/* Ticket #77 — périmètre affiché (MANAGER / ADMIN) : mêmes tags que
            la saisie des temps. */}
        {canScopeTeam ? (
          <div style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
            {[
              { key: 'all', label: 'Tout le monde' },
              { key: 'team', label: 'Mon équipe' },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setPersonScope(s.key)}
                aria-pressed={personScope === s.key}
                title={`Afficher : ${s.label}`}
                style={{
                  padding: '4px 12px',
                  background: personScope === s.key ? 'var(--accent)' : 'transparent',
                  color: personScope === s.key ? '#fff' : 'var(--text)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        ) : null}

        <div style={{ marginLeft: 12, display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <FilterBar scope="schedule" />
        </div>

        {!canScheduleAny ? (
          <span
            title="La création / édition d'allocations n'est pas activée pour votre compte"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginLeft: 12,
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass)',
              color: 'var(--text-muted)',
              fontSize: 12,
            }}
          >
            <Info size={12} />
            Lecture seule
          </span>
        ) : null}

        {/* Ticket #34 — flexShrink: 0 : la navigation de période ne doit jamais
            être compressée par une chip de filtre au libellé long. */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          <button type="button" onClick={() => shift(-1)} style={navBtn} title="Période précédente">
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={gotoToday}
            style={{ ...navBtn, padding: '4px 12px', width: 'auto' }}
            title="Revenir à aujourd'hui"
          >
            Aujourd&apos;hui
          </button>
          <button type="button" onClick={() => shift(1)} style={navBtn} title="Période suivante">
            <ChevronRight size={16} />
          </button>
          <span
            style={{
              marginLeft: 12,
              color: 'var(--text)',
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              // Largeur FIXE (cas le plus long : à cheval sur deux années) :
              // un libellé de largeur variable décalait les flèches et
              // « Aujourd'hui » à chaque clic → clic par erreur.
              width: '16.5em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtPeriodTitle(visibleFrom, visibleTo)}
          </span>
        </div>
      </div>

      {error ? (
        <div style={{ padding: '12px 24px', color: 'var(--danger)' }}>{error}</div>
      ) : null}

      {/* Ticket #91 — chargement silencieux (glissement, SSE) : barre fine. */}
      <div style={{ position: 'relative', height: 0 }}>
        <TimelineProgress active={fetching && !loading} />
      </div>

      {/* Container scrollable principal */}
      <div
        ref={containerRef}
        {...containerProps}
        onContextMenu={openToolMenu}
        style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Header sticky — zIndex 10 pour rester au-dessus des blocs
            Allocation/TimeOff (zIndex 4) qui passaient autrement par-dessus
            la ligne d'en-tête au scroll horizontal. */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            background: 'var(--card-bg)',
            borderBottom: '1px solid var(--glass-border)',
            height: HEADER_HEIGHT_PX,
            width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
          }}
        >
          {/* Coin haut-gauche (sticky sur deux axes) — zIndex 11 pour rester
              au-dessus de la sticky col gauche (zIndex 8) au scroll.
              Sprint 8.3 : devient un bouton qui toggle le tri asc/desc, état
              persisté en localStorage. */}
          <button
            type="button"
            onClick={togglePersonSort}
            title={`Cliquer pour trier par : Nom A→Z → Nom Z→A → Prénom A→Z → Prénom Z→A (actuel : ${
              personSort.field === 'lastName' ? 'Nom' : 'Prénom'
            } ${personSort.dir === 'asc' ? 'A→Z' : 'Z→A'})${
              personOrder ? ' · ordre manuel actif — cliquer pour le réinitialiser' : ''
            }`}
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 11,
              width: LEFT_COL_WIDTH_PX,
              flex: '0 0 auto',
              background: 'var(--card-bg)',
              borderRight: '1px solid var(--glass-border)',
              borderTop: 'none',
              borderLeft: 'none',
              borderBottom: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 6,
              padding: '0 12px',
              fontSize: 12,
              color: 'var(--text-muted)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span>Personnes ({persons.length})</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              {personOrder ? (
                <>
                  <GripVertical size={13} aria-hidden />
                  <span>Manuel</span>
                </>
              ) : (
                <>
                  <span>{personSort.field === 'lastName' ? 'Nom' : 'Prénom'}</span>
                  {personSort.dir === 'asc' ? (
                    <ArrowDownAZ size={14} aria-label="A→Z" />
                  ) : (
                    <ArrowDownZA size={14} aria-label="Z→A" />
                  )}
                </>
              )}
            </span>
          </button>

          {/* Bandeau jours OU mois selon zoom */}
          <div
            style={{
              position: 'relative',
              flex: '1 0 auto',
              width: totalDays * dayWidth,
              height: HEADER_HEIGHT_PX,
            }}
          >
            {/* Ticket #50 — bande mois systématique en haut, puis bande
                semaines (zoom mois) ou colonnes de jours (zoom jour/semaine). */}
            {[
              // Ticket #50 — bande mois : découpée par mois en zoom mois, mais
              // par SEMAINE en zoom jour/semaine, où un mois est bien plus
              // large que la viewport et son libellé unique quittait l'écran.
              ...(zoom === 'month' ? monthGroups ?? [] : weekGroups ?? []).map((g) => {
                const longLabel = zoom === 'month' ? g.label : g.monthLabel;
                const shortLabel = zoom === 'month' ? g.shortLabel : g.monthLabelShort;
                // Libellé « sticky » : centré sur la portion VISIBLE du groupe
                // et non sur le groupe entier, pour rester lisible tant qu'un
                // bout du groupe est à l'écran.
                const visStart = Math.max(g.left, scrollLeft);
                const visEnd = Math.min(g.left + g.width, scrollLeft + visibleWidth);
                const visWidth = Math.max(0, visEnd - visStart);
                return (
                  <div
                    key={`m-${toISODate(addDays(period.from, g.startIdx))}`}
                    data-row-type="month-header"
                    style={{
                      position: 'absolute',
                      left: g.left,
                      top: 0,
                      width: g.width,
                      height: MONTH_BAND_HEIGHT_PX,
                      borderRight: '1px solid var(--glass-border)',
                      borderBottom: '1px solid var(--glass-border)',
                      background: 'var(--glass)',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                    }}
                    title={longLabel}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: (visStart + visEnd) / 2 - g.left,
                        top: 0,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        color: 'var(--text)',
                      }}
                    >
                      {visWidth >= 110 ? longLabel : visWidth >= 52 ? shortLabel : ''}
                    </span>
                  </div>
                );
              }),
              ...(zoom === 'month' ? weekGroups ?? [] : []).map((w) => (
                <div
                  key={`w-${toISODate(addDays(period.from, w.startIdx))}`}
                  data-row-type="week-header"
                  style={{
                    position: 'absolute',
                    left: w.left,
                    top: MONTH_BAND_HEIGHT_PX,
                    width: w.width,
                    height: HEADER_HEIGHT_PX - MONTH_BAND_HEIGHT_PX,
                    borderRight: '1px solid var(--glass-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                  title={w.label}
                >
                  {w.label}
                </div>
              )),
              ...(zoom === 'month'
                ? []
                : Array.from({ length: lastVisibleDay - firstVisibleDay })
              ).map((_, i) => {
                const dayIndex = firstVisibleDay + i;
                const d = addDays(period.from, dayIndex);
                const isToday = dayIndex === todayIndex;
                const weekend = isWeekend(d);
                const dow = (d.getDay() + 6) % 7;
                return (
                  <div
                    key={toISODate(d)}
                    data-row-type="day-header"
                    data-is-today={isToday ? 'true' : 'false'}
                    style={{
                      position: 'absolute',
                      left: dayIndex * dayWidth,
                      top: MONTH_BAND_HEIGHT_PX,
                      width: dayWidth,
                      height: HEADER_HEIGHT_PX - MONTH_BAND_HEIGHT_PX,
                      borderRight: '1px solid var(--glass-border)',
                      background: isToday
                        ? 'var(--accent)'
                        : weekend
                        ? 'var(--glass)'
                        : 'transparent',
                      color: isToday ? '#fff' : weekend ? 'var(--text-muted)' : 'var(--text)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: zoom === 'day' ? 11 : 9,
                      lineHeight: 1.2,
                      boxSizing: 'border-box',
                    }}
                    title={`${DAYS_FR_LONG[dow]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`}
                  >
                    {zoom === 'day' ? (
                      <>
                        <span style={{ opacity: 0.7 }}>{DAYS_FR_SHORT[dow]}</span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{d.getDate()}</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 500 }}>{d.getDate()}</span>
                    )}
                  </div>
                );
              }),
            ]}
          </div>
        </div>

        {/* Corps (grille personnes × jours) */}
        <div
          style={{
            position: 'relative',
            width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
            height: totalGridHeight,
          }}
        >
          {loading ? (
            <div
              style={{
                position: 'absolute',
                left: LEFT_COL_WIDTH_PX,
                top: 0,
                padding: 20,
                color: 'var(--text-muted)',
              }}
            >
              Chargement du planning…
            </div>
          ) : null}

          {persons.slice(firstVisibleRow, lastVisibleRow).map((person, idx) => {
            const rowIndex = firstVisibleRow + idx;
            const top = rowTops[rowIndex];
            const laneInfo = laneDataByPerson.get(person.id);
            const personRowHeight = rowHeights[rowIndex];
            const personAllocs = allocByPerson.get(person.id) ?? EMPTY_LIST;
            const personTimeOffs = timeOffsByPerson.get(person.id) ?? EMPTY_LIST;
            const personHolidays = holidaysByPersonIndex.get(person.id) ?? null;
            const isDraggingThisRow = drag?.personId === person.id;
            return (
              <PersonRow
                key={person.id}
                person={person}
                rowIndex={rowIndex}
                top={top}
                rowHeight={personRowHeight}
                laneHeight={laneHeight}
                lanes={laneInfo}
                dayWidth={dayWidth}
                periodFrom={period.from}
                totalDays={totalDays}
                allocations={personAllocs}
                timeOffs={personTimeOffs}
                holidays={personHolidays}
                todayIndex={todayIndex}
                firstVisibleDay={firstVisibleDay}
                lastVisibleDay={lastVisibleDay}
                isDragging={isDraggingThisRow}
                dragGeom={isDraggingThisRow ? dragGeom : null}
                canCreate={
                  (canCreate && canScheduleForPerson(person.id)) ||
                  (zoom !== 'month' &&
                    !isDeleteMode &&
                    (canDeclareUnavailability(person.id) || canDeclarePartTime(person.id)))
                }
                canEditAllocations={canEditAllocations && canScheduleForPerson(person.id)}
                zoom={zoom}
                move={move}
                resize={resize}
                vresize={vresize}
                onCellPointerDown={rowHandlers.onCellPointerDown}
                onCellPointerEnter={rowHandlers.onCellPointerEnter}
                onAllocationPointerDown={rowHandlers.onAllocationPointerDown}
                onResizeHandlePointerDown={rowHandlers.onResizeHandlePointerDown}
                onVresizeHandlePointerDown={rowHandlers.onVresizeHandlePointerDown}
                canResizeTimeOff={canResizeTimeOffInRow}
                onTimeOffVresizePointerDown={rowHandlers.onTimeOffVresizePointerDown}
                deleteMode={isDeleteMode}
                onSelectAllocation={rowHandlers.onSelectAllocation}
                onSelectTimeOff={rowHandlers.onSelectTimeOff}
                onSelectPerson={isAdmin ? rowHandlers.onSelectPerson : undefined}
                onReorderPerson={rowHandlers.onReorderPerson}
                workLocByDate={workLocByPerson.get(person.id) ?? null}
                canSetStatus={canScheduleForPerson(person.id)}
                onStatusClick={rowHandlers.onStatusClick}
              />
            );
          })}

          {/* Empty state */}
          {!loading && totalRows === 0 ? (
            <div
              style={{
                position: 'absolute',
                left: LEFT_COL_WIDTH_PX + 20,
                top: 20,
                color: 'var(--text-muted)',
              }}
            >
              Aucune personne à afficher.
            </div>
          ) : null}
        </div>
      </div>

      {/* Modale détail / édition */}
      {detail?.type === 'allocation' && canScheduleForPerson(detail.item?.personId) ? (
        <EditAllocationModal
          open
          allocation={detail.item}
          person={detail.person}
          affaires={targetAffaires}
          affairesError={targetAffairesError}
          onClose={() => setDetail(null)}
          onSaved={() => {
            setDetail(null);
            setReloadKey((k) => k + 1);
          }}
          onError={(msg) => setToast({ message: msg, kind: 'error' })}
        />
      ) : detail?.type === 'timeoff' &&
        (isAdmin ||
          isManager ||
          (detail.item?.timeOffType?.name === UNAVAILABILITY_TYPE_NAME &&
            canDeclareUnavailability(detail.person?.id)) ||
          (detail.item?.timeOffType?.name === PART_TIME_TYPE_NAME &&
            ownsPartTime(detail.person?.id))) ? (
        <TimeOffDetailModal
          open
          item={detail.item}
          person={detail.person}
          canDelete={
            isAdmin ||
            manageablePersonIds.has(detail.person?.id) ||
            (detail.item?.timeOffType?.name === UNAVAILABILITY_TYPE_NAME &&
              canDeclareUnavailability(detail.person?.id)) ||
            (detail.item?.timeOffType?.name === PART_TIME_TYPE_NAME &&
              ownsPartTime(detail.person?.id))
          }
          onClose={() => setDetail(null)}
          onDeleted={() => {
            setDetail(null);
            setReloadKey((k) => k + 1);
          }}
          onError={(msg) => setToast({ message: msg, kind: 'error' })}
        />
      ) : (
        <Modal
          open={Boolean(detail)}
          onClose={() => setDetail(null)}
          title={
            detail?.type === 'allocation'
              ? 'Détail allocation'
              : detail?.type === 'timeoff'
              ? 'Détail absence'
              : ''
          }
        >
          {detail ? <DetailContent detail={detail} /> : null}
        </Modal>
      )}

      {/* Édition Person depuis la colonne de gauche (clic sur le nom — ADMIN) */}
      <PersonFormModal
        open={Boolean(editPersonId)}
        personId={editPersonId}
        onClose={() => setEditPersonId(null)}
        onSaved={() => {
          setEditPersonId(null);
          setReloadKey((k) => k + 1);
        }}
      />

      {/* Modale création (Sprint 2.C + phase picker 2.D) */}
      <CreateAllocationModal
        open={Boolean(createDraft)}
        draft={createDraft}
        periodFrom={period.from}
        persons={persons}
        schedulablePersons={schedulablePersons}
        affaires={targetAffaires}
        affairesError={targetAffairesError}
        canCreateTimeOff={isAdmin || (createDraft ? canDeclarePartTime(createDraft.personId) : false)}
        canCreateAllocation={createDraft ? canScheduleForPerson(createDraft.personId) : false}
        canDeclareUnavailability={
          createDraft ? canDeclareUnavailability(createDraft.personId) : false
        }
        unavailabilityForOther={Boolean(createDraft && createDraft.personId !== myPersonId)}
        overbookCtx={overbookCtx}
        requestOverbookConfirm={setPendingOverbook}
        onClose={() => setCreateDraft(null)}
        onCreated={() => {
          setCreateDraft(null);
          setReloadKey((k) => k + 1);
        }}
      />

      {/* Ticket #6 — Modale statut de présence / lieu de travail (par jour). */}
      <StatusModal
        ctx={statusModal}
        persons={schedule.persons}
        onClose={() => setStatusModal(null)}
        onSaved={() => {
          setStatusModal(null);
          setReloadKey((k) => k + 1);
        }}
        onError={(m) => setToast({ message: m, kind: 'error' })}
      />

      {/* Sprint 24 — confirmation de surbooking (non bloquant côté backend) */}
      <Modal
        open={Boolean(pendingOverbook)}
        onClose={() => setPendingOverbook(null)}
        title="Surbooking détecté"
      >
        {pendingOverbook && (
          <div style={{ fontSize: 13, color: 'var(--text)' }}>
            <p style={{ marginTop: 0 }}>
              {pendingOverbook.plural
                ? `${pendingOverbook.personName} dépasseront leur capacité disponible sur`
                : `${pendingOverbook.personName} dépassera sa capacité disponible sur`}
              &nbsp;:
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 18 }}>
              {pendingOverbook.days.map((d) => (
                <li key={`${d.personName ?? ''}|${d.iso}`} style={{ marginBottom: 2 }}>
                  {d.personName ? `${d.personName} — ` : null}
                  <strong>{d.allocated}h</strong> / {d.capacity}h le {fmtDayShort(d.iso)}
                </li>
              ))}
            </ul>
            <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Continuer malgré tout&nbsp;?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setPendingOverbook(null)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--glass-border)',
                  background: 'var(--glass)',
                  color: 'var(--text)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  const cb = pendingOverbook.onConfirm;
                  setPendingOverbook(null);
                  cb?.();
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--warning)',
                  background: 'var(--warning)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Ticket #62 — menu contextuel des modes + périmètre de suppression */}
      <ToolModeContextMenu
        position={toolMenu}
        modes={TOOL_MODES}
        mode={toolMode}
        onChange={setToolMode}
        onClose={closeToolMenu}
      />
      <EditScopeDialog
        open={pendingDelete !== null}
        mode="delete"
        entityLabel="allocation"
        onCancel={() => setPendingDelete(null)}
        onConfirm={(scope) => {
          const alloc = pendingDelete;
          setPendingDelete(null);
          if (alloc) performDeleteAllocation(alloc, scope);
        }}
      />

      {/* Ticket #48 — heures d'un congé récurrent : périmètre de la modification */}
      <EditScopeDialog
        open={pendingTimeOffHours !== null}
        mode="edit"
        entityLabel="congé"
        onCancel={() => setPendingTimeOffHours(null)}
        onConfirm={(scope) => {
          const pending = pendingTimeOffHours;
          setPendingTimeOffHours(null);
          if (pending) saveTimeOffHours(pending.timeOff, pending.data, scope);
        }}
      />

      {/* Toast */}
      <Toast
        message={toast?.message}
        kind={toast?.kind}
        onClose={() => setToast(null)}
      />
    </div>
  );
}

// ---------- Sous-composants ----------

const navBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 4,
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  cursor: 'pointer',
  flexShrink: 0,
};

/**
 * Ticket #91 — cellule jour d'une ligne du Planning, mémoïsée : quand la plage
 * rendue avance d'un bloc de jours, seules les cellules nouvelles sont
 * rendues (les autres ont les mêmes props). Tous les calculs restent dans
 * `PersonRow` ; ne passer ici que des valeurs stables (primitives, objets
 * issus des index mémoïsés, gestionnaires à identité stable).
 */
const DayCell = memo(function DayCell({
  personId,
  iso,
  dayIndex,
  dayWidth,
  rowHeight,
  zoom,
  isToday,
  isHoliday,
  holiday,
  holidayScope,
  cellCanDrag,
  hatchBg,
  outOfEmploymentTitle,
  isOutOfEmployment,
  isPartTimeDay,
  weekend,
  isNonWorkingDay,
  day,
  hasTimeOff,
  canSetStatus,
  onCellPointerDown,
  onCellPointerEnter,
  onStatusClick,
}) {
  return (
    <div
      data-row-type="day-cell"
      data-is-today={isToday ? 'true' : 'false'}
      data-is-holiday={isHoliday ? 'true' : 'false'}
      data-can-drag={cellCanDrag ? 'true' : 'false'}
      onPointerDown={
        cellCanDrag
          ? (e) => {
              if (e.button !== undefined && e.button !== 0) return;
              e.preventDefault();
              onCellPointerDown(personId, dayIndex, e);
            }
          : undefined
      }
      onPointerEnter={
        cellCanDrag
          ? () => onCellPointerEnter(personId, dayIndex)
          : undefined
      }
      title={
        outOfEmploymentTitle
          ? outOfEmploymentTitle
          : isHoliday
          ? `${holiday.name} (${holidayScope})`
          : isPartTimeDay
          ? 'Temps partiel — jour non travaillé'
          : undefined
      }
      data-out-of-employment={isOutOfEmployment ? 'true' : 'false'}
      style={{
        position: 'absolute',
        left: dayIndex * dayWidth,
        top: 0,
        width: dayWidth,
        height: rowHeight,
        borderRight: '1px solid var(--glass-border)',
        background: hatchBg,
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        touchAction: cellCanDrag ? 'none' : 'auto',
      }}
    >
      {/* Ticket #6 — bandeau statut de présence (cliquable, même sans
          allocation). Journée = bande pleine ; sinon demi-cases
          matin (gauche) / après-midi (droite). « + » sur la demi vide. */}
      {(() => {
        // `day` : statut de présence du jour (prop).
        const hasAny = day && (day.morning || day.afternoon);
        // On masque le « + » d'ajout de statut sur les jours non
        // travaillés (week-end, temps partiel, jour non travaillé de la
        // personne). Les statuts déjà posés restent affichés.
        const suppressEmptyAdd =
          weekend || isNonWorkingDay || isPartTimeDay || hasTimeOff;
        if (!hasAny && (!canSetStatus || isOutOfEmployment || suppressEmptyAdd))
          return null;

        const renderHalf = (slotKey, wl, left) => {
          if (!wl && (!canSetStatus || suppressEmptyAdd)) return null;
          const halfArg = slotKey; // 'morning' | 'afternoon'
          return (
            <div
              key={slotKey}
              className={`wl-band${wl ? '' : ' wl-band--empty'}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (canSetStatus) onStatusClick(personId, iso, halfArg, wl ?? null);
              }}
              title={
                wl
                  ? `${left ? 'Matin' : 'Après-midi'} : ${WORK_LOCATION_LABELS[wl.location] ?? wl.location}${wl.isRecurring ? ' (récurrent)' : ''}`
                  : `Définir un statut (${left ? 'matin' : 'après-midi'})`
              }
              style={{
                left: left ? 0 : dayWidth / 2,
                width: dayWidth / 2,
                cursor: canSetStatus ? 'pointer' : 'default',
                background: wl ? WORK_LOCATION_COLORS[wl.location] ?? 'var(--accent)' : 'transparent',
                borderRight: left ? '1px solid rgba(255,255,255,0.35)' : undefined,
              }}
            >
              {wl ? (dayWidth >= 60 ? WORK_LOCATION_SHORT[wl.location] ?? '' : '•') : '+'}
            </div>
          );
        };

        if (day && day.full) {
          // Statut « journée » → bande pleine, clic = modifier (half full).
          const wl = day.morning;
          return (
            <div
              className="wl-band"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                if (canSetStatus) onStatusClick(personId, iso, 'full', wl);
              }}
              title={`Journée : ${WORK_LOCATION_LABELS[wl.location] ?? wl.location}${wl.isRecurring ? ' (récurrent)' : ''}`}
              style={{
                width: dayWidth,
                cursor: canSetStatus ? 'pointer' : 'default',
                background: WORK_LOCATION_COLORS[wl.location] ?? 'var(--accent)',
              }}
            >
              {dayWidth >= 40 ? WORK_LOCATION_SHORT[wl.location] ?? '' : '•'}
            </div>
          );
        }
        return (
          <>
            {renderHalf('morning', day?.morning ?? null, true)}
            {renderHalf('afternoon', day?.afternoon ?? null, false)}
          </>
        );
      })()}
      {/* Libellé "Temps partiel" centré (vertical) sur un jour non
          travaillé de la personne — uniquement si la cellule est assez
          large (zoom jour). */}
      {isPartTimeDay && dayWidth >= 40 ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              transform: 'rotate(-90deg)',
              whiteSpace: 'nowrap',
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              opacity: 0.8,
            }}
          >
            Temps partiel
          </span>
        </div>
      ) : null}
      {/* Pastille discrète en zoom mois (cellule trop étroite pour le hatch). */}
      {isHoliday && zoom === 'month' ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 6,
            height: 6,
            borderRadius: 999,
            background: 'var(--warning)',
          }}
        />
      ) : null}
    </div>
  );
});

const PersonRow = memo(function PersonRow({
  person,
  rowIndex,
  top,
  rowHeight,
  laneHeight,
  lanes,
  dayWidth,
  periodFrom,
  totalDays,
  allocations,
  timeOffs,
  holidays,
  todayIndex,
  firstVisibleDay,
  lastVisibleDay,
  isDragging,
  dragGeom,
  canCreate,
  canEditAllocations,
  zoom,
  move,
  resize,
  vresize,
  onCellPointerDown,
  onCellPointerEnter,
  onAllocationPointerDown,
  onResizeHandlePointerDown,
  onVresizeHandlePointerDown,
  canResizeTimeOff,
  onTimeOffVresizePointerDown,
  onSelectAllocation,
  deleteMode = false,
  onSelectTimeOff,
  onSelectPerson,
  onReorderPerson,
  workLocByDate,
  canSetStatus,
  onStatusClick,
}) {
  // Capacité quotidienne moyenne de la Person (depuis workDaysHours Float).
  // Sert à dimensionner les blocs Allocation en proportion : une allocation
  // 2h/j sur capa 8h = bloc à 25% de hauteur.
  const dailyCapacity = computeDailyCapacity(person.workDaysHours);
  const workingDayMask = useMemo(
    () => getWorkingDayMask(person.workDaysHours),
    [person.workDaysHours],
  );
  // Sprint 8.3 — indices de début / fin d'embauche dans la grille, pour
  // griser la zone avant `startDate` (pré-embauche) ou après `endDate`
  // (post-sortie). Null si la borne n'est pas définie côté Person.
  const personStartIdx = useMemo(() => {
    if (!person.startDate) return null;
    return diffDays(parseISODate(String(person.startDate).slice(0, 10)), periodFrom);
  }, [person.startDate, periodFrom]);
  const personEndIdx = useMemo(() => {
    if (!person.endDate) return null;
    return diffDays(parseISODate(String(person.endDate).slice(0, 10)), periodFrom);
  }, [person.endDate, periodFrom]);
  // Plages de dates des TimeOffs **fullDay** de cette Person — utilisées pour
  // masquer entièrement les allocations chevauchées (priorité TimeOff > Allocation,
  // conforme au comportement Float). Les TimeOff partiels (ex: 4h/j sur capa 8h)
  // **ne masquent PAS** l'allocation : Float affiche les deux blocs côte-à-côte,
  // l'allocation 4h en bas et le TimeOff partiel 4h en haut (cf. stacking ci-dessous).
  const timeOffRanges = useMemo(
    () =>
      timeOffs
        .filter((to) => isFullDayLeave(to))
        .map((to) => ({
          startMs: parseISODate(to.startDate).getTime(),
          endMs: parseISODate(to.endDate).getTime(),
        })),
    [timeOffs],
  );
  // Allocations fragmentées : 1 entrée par segment contigu (week-ends et
  // TimeOffs exclus). Chaque segment a un id synthétique
  // `${alloc.id}#${segIdx}` pour la key React + une référence à l'alloc
  // d'origine (pour drag/edit qui agit sur l'allocation entière).
  const allocationSegments = useMemo(() => {
    const out = [];
    for (const a of allocations) {
      const segs = splitAllocationIntoSegments(a, workingDayMask, timeOffRanges, holidays);
      for (let i = 0; i < segs.length; i++) {
        out.push({
          ...a,
          // On override startDate/endDate par les bornes du segment pour le
          // rendu géométrique. L'allocation source reste accessible via
          // `_sourceAllocation` pour les actions edit/move/resize.
          startDate: segs[i].startDate,
          endDate: segs[i].endDate,
          _segmentId: `${a.instanceId ?? a.id}#${i}`,
          _sourceAllocation: a,
          _startIdx: diffDays(parseISODate(segs[i].startDate), periodFrom),
          _endIdx: diffDays(parseISODate(segs[i].endDate), periodFrom),
        });
      }
    }
    return out;
  }, [allocations, workingDayMask, timeOffRanges, holidays, periodFrom]);
  // Voie (lane) verticale de chaque item, calculée par le parent
  // (computeRowLanes) et passée via `lanes`. Allocation/TimeOff sont placés sur
  // leur voie respective ; les chevauchements restent tous visibles (la ligne
  // a été agrandie en conséquence).
  // Offset vertical (px) du haut de la voie de l'item, = somme des hauteurs
  // des voies inférieures (empilement contigu). Voir computeRowLanes.
  const laneTopPxOf = useCallback(
    (key) => (lanes?.topByKey?.get(key) ?? 0) * laneHeight,
    [lanes, laneHeight],
  );
  // Fragmentation des TimeOffs : un congé de 2 semaines doit s'afficher
  // comme 2 segments lun-ven (skip WE intermédiaires) plutôt qu'un bloc
  // continu qui couvre visuellement les samedis/dimanches. On passe une
  // liste vide pour timeOffRanges (pas de masquage d'un TimeOff par un autre)
  // ET on passe les jours fériés pour casser le segment dessus (un jour férié
  // n'est pas décompté du quota de congés — Float UX).
  const timeOffSegments = useMemo(() => {
    const out = [];
    for (const t of timeOffs) {
      const segs = splitAllocationIntoSegments(t, workingDayMask, [], holidays);
      for (let i = 0; i < segs.length; i++) {
        out.push({
          ...t,
          startDate: segs[i].startDate,
          endDate: segs[i].endDate,
          _segmentId: `${t.instanceId ?? t.id}#${i}`,
          _sourceTimeOff: t,
          _startIdx: diffDays(parseISODate(segs[i].startDate), periodFrom),
          _endIdx: diffDays(parseISODate(segs[i].endDate), periodFrom),
        });
      }
    }
    return out;
  }, [timeOffs, workingDayMask, holidays, periodFrom]);
  // Ticket #91 — virtualisation horizontale des blocs : seuls ceux qui touchent
  // la plage rendue sont dessinés (la fenêtre chargée couvre cinq écrans, soit
  // jusqu'à 350 jours de blocs par ligne en zoom Mois). Pendant un geste, tout
  // reste rendu : le bloc déplacé peut sortir de sa position d'origine.
  const anyGesture = Boolean(move || resize || vresize);
  const inRenderedRange = (seg) =>
    anyGesture || (seg._endIdx >= firstVisibleDay - 1 && seg._startIdx <= lastVisibleDay);
  // Ensemble des dates ISO couvertes par un congé (TimeOff) de cette Person —
  // y compris les congés PARTIELS (ex. « Temps Partiel » récurrent). Sert à
  // masquer le « + » d'ajout de statut de présence sur ces jours (un jour en
  // congé n'a pas à proposer la pose d'un statut de présence).
  const timeOffIsoSet = useMemo(() => {
    const set = new Set();
    for (const t of timeOffs) {
      const start = parseISODate(String(t.startDate).slice(0, 10));
      const end = parseISODate(String(t.endDate).slice(0, 10));
      const span = diffDays(end, start);
      for (let k = 0; k <= span; k++) set.add(toISODate(addDays(start, k)));
    }
    return set;
  }, [timeOffs]);
  return (
    <div
      data-row-type="person"
      data-floatid={person.id}
      data-item-index={rowIndex}
      data-is-dragging={isDragging ? 'true' : 'false'}
      style={{
        position: 'absolute',
        top,
        left: 0,
        height: rowHeight,
        width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
        borderBottom: '1px solid var(--glass-border)',
        display: 'flex',
        // Ticket #91 — la mise en page d'une ligne n'affecte pas les autres :
        // le navigateur ne recalcule que la ligne modifiée (pas de `paint`,
        // qui rognerait les blocs en cours de déplacement).
        contain: 'layout style',
      }}
    >
      {/* Col gauche sticky — zIndex 8 pour rester au-dessus des blocs
          Allocation/TimeOff (zIndex 4) au scroll horizontal.
          #19 — draggable pour réordonner les personnes (drop sur une autre). */}
      <div
        draggable={Boolean(onReorderPerson)}
        onDragStart={
          onReorderPerson
            ? (e) => {
                e.dataTransfer.setData('text/plain', person.id);
                e.dataTransfer.effectAllowed = 'move';
              }
            : undefined
        }
        onDragOver={onReorderPerson ? (e) => e.preventDefault() : undefined}
        onDrop={
          onReorderPerson
            ? (e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                onReorderPerson(id, person.id);
              }
            : undefined
        }
        title={onReorderPerson ? 'Glisser pour réordonner' : undefined}
        style={{
          position: 'sticky',
          left: 0,
          zIndex: 8,
          width: LEFT_COL_WIDTH_PX,
          flex: '0 0 auto',
          background: 'var(--card-bg)',
          borderRight: '1px solid var(--glass-border)',
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          boxSizing: 'border-box',
          cursor: onReorderPerson ? 'grab' : 'default',
        }}
      >
        {onReorderPerson ? (
          <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} aria-hidden />
        ) : null}
        <Avatar person={person} size={28} title={displayPersonName(person)} />
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, flex: 1 }}>
        {onSelectPerson ? (
          <button
            type="button"
            onClick={() => onSelectPerson(person)}
            title={`Modifier ${displayPersonName(person)}`}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              font: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--accent)',
              cursor: 'pointer',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {displayPersonName(person)}
          </button>
        ) : (
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayPersonName(person)}
          >
            {displayPersonName(person)}
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {person.role?.name ?? person.jobTitle ?? '—'}
        </div>
        {/* Sprint 8.3 — badge "Sorti·e le DD/MM/YYYY" si endDate < today. */}
        {personEndIdx != null && personEndIdx < todayIndex ? (
          <div
            style={{
              fontSize: 10,
              color: 'var(--danger)',
              fontWeight: 500,
              marginTop: 2,
            }}
            title={`Date de sortie : ${String(person.endDate).slice(0, 10)}`}
          >
            Sorti·e le {String(person.endDate).slice(0, 10)}
          </div>
        ) : null}
        </div>
      </div>

      {/* Zone scrollable des jours — fond gridé + blocs */}
      <div
        style={{
          position: 'relative',
          flex: '1 0 auto',
          width: totalDays * dayWidth,
          height: rowHeight,
        }}
      >
        {/* Cellules vides (uniquement visibles) — fond weekend / today + drag handlers.
            Sprint 5 : si la cellule correspond à un jour férié pour cette Person,
            overlay hatch + tooltip et désactivation du drag-to-create
            (cohérence UX : pas de planification sur un férié). */}
        {Array.from({ length: lastVisibleDay - firstVisibleDay }).map((_, i) => {
          const dayIndex = firstVisibleDay + i;
          const d = addDays(periodFrom, dayIndex);
          const isToday = dayIndex === todayIndex;
          const weekend = isWeekend(d);
          // Jour non ouvré pour CETTE personne (depuis `workDaysHours` Float) :
          // p.ex. un consultant à temps partiel dont le mercredi est à 0h. On le
          // grise comme un weekend (cohérence avec splitAllocationIntoSegments qui
          // n'affiche aucune allocation sur ces jours).
          const isNonWorkingDay = !workingDayMask[d.getDay()];
          // Temps partiel = jour non ouvré qui n'est PAS un weekend (le weekend
          // reste gris plat, sans hachure ni libellé). On le rend hachuré +
          // libellé "Temps partiel".
          const isPartTimeDay = isNonWorkingDay && !weekend;
          const iso = toISODate(d);
          const holiday = holidays ? holidays.get(iso) : null;
          const isHoliday = Boolean(holiday);
          const holidayScope = holiday && holiday.regionCode ? 'régional' : 'national';
          // Sprint 8.3 — zone hors période d'embauche (pré-embauche /
          // post-sortie). Pas de drag-to-create possible et overlay foncé.
          const isBeforeHire = personStartIdx != null && dayIndex < personStartIdx;
          const isAfterExit = personEndIdx != null && dayIndex > personEndIdx;
          const isOutOfEmployment = isBeforeHire || isAfterExit;
          const cellCanDrag = canCreate && !isHoliday && !isOutOfEmployment && !isNonWorkingDay;
          // Background : out-of-employment > férié > today > weekend > transparent.
          const baseBg = isOutOfEmployment
            ? 'rgba(0,0,0,0.18)'
            : isHoliday
            ? 'rgba(120,120,120,0.08)'
            : isToday
            ? 'rgba(50, 100, 150, 0.08)'
            : weekend || isNonWorkingDay
            ? 'var(--glass)'
            : 'transparent';
          const hatchBg = isOutOfEmployment
            ? `repeating-linear-gradient(45deg, rgba(0,0,0,0.10), rgba(0,0,0,0.10) 4px, transparent 4px, transparent 8px), ${baseBg}`
            : isHoliday
            ? `repeating-linear-gradient(45deg, rgba(0,0,0,0.06), rgba(0,0,0,0.06) 4px, transparent 4px, transparent 8px), ${baseBg}`
            : isPartTimeDay
            ? `repeating-linear-gradient(45deg, rgba(0,0,0,0.22), rgba(0,0,0,0.22) 5px, transparent 5px, transparent 10px), var(--glass)`
            : baseBg;
          const outOfEmploymentTitle = isAfterExit
            ? `Sortie le ${String(person.endDate).slice(0, 10)}`
            : isBeforeHire
            ? `Embauche le ${String(person.startDate).slice(0, 10)}`
            : undefined;
          return (
            <DayCell
              key={iso}
              personId={person.id}
              iso={iso}
              dayIndex={dayIndex}
              dayWidth={dayWidth}
              rowHeight={rowHeight}
              zoom={zoom}
              isToday={isToday}
              isHoliday={isHoliday}
              holiday={holiday}
              holidayScope={holidayScope}
              cellCanDrag={cellCanDrag}
              hatchBg={hatchBg}
              outOfEmploymentTitle={outOfEmploymentTitle}
              isOutOfEmployment={isOutOfEmployment}
              isPartTimeDay={isPartTimeDay}
              weekend={weekend}
              isNonWorkingDay={isNonWorkingDay}
              day={workLocByDate ? workLocByDate.get(iso) ?? null : null}
              hasTimeOff={timeOffIsoSet.has(iso)}
              canSetStatus={canSetStatus}
              onCellPointerDown={onCellPointerDown}
              onCellPointerEnter={onCellPointerEnter}
              onStatusClick={onStatusClick}
            />
          );
        })}

        {/* Allocations — fragmentées par semaine (WE skip) et par TimeOff
            chevauché (priorité TimeOff > Allocation). Une même allocation
            peut produire plusieurs segments rendus séparément, partageant
            la même alloc source (drag/edit agit sur l'allocation entière). */}
        {allocationSegments.filter(inRenderedRange).map((seg) => {
          const sourceAlloc = seg._sourceAllocation;
          // Si l'allocation source est actuellement en move/resize, on
          // applique l'offset visuel sur le segment (les autres segments de
          // la même alloc bougent aussi cohérent).
          let overrideStartIdx = null;
          let overrideEndIdx = null;
          let isMoving = false;
          let isResizing = false;
          const moveMatches = move
            ? move.instanceId
              ? move.instanceId === sourceAlloc.instanceId
              : move.allocationId === sourceAlloc.id
            : false;
          const resizeMatches = resize
            ? resize.instanceId
              ? resize.instanceId === sourceAlloc.instanceId
              : resize.allocationId === sourceAlloc.id
            : false;
          if (moveMatches) {
            const baseStart = diffDays(parseISODate(seg.startDate), periodFrom);
            const baseEnd = diffDays(parseISODate(seg.endDate), periodFrom);
            overrideStartIdx = baseStart + move.deltaDay;
            overrideEndIdx = baseEnd + move.deltaDay;
            isMoving = true;
          } else if (resizeMatches) {
            overrideStartIdx = resize.currentStartIdx;
            overrideEndIdx = resize.currentEndIdx;
            isResizing = true;
          }
          // Ticket #48 — aperçu live du resize vertical (heures/jour).
          const vresizeMatches = vresize?.kind === 'allocation'
            ? vresize.instanceId
              ? vresize.instanceId === sourceAlloc.instanceId
              : vresize.allocationId === sourceAlloc.id
            : false;
          const overrideHours = vresizeMatches ? vresize.currentHours : null;
          return (
            <AllocationBlock
              key={seg._segmentId}
              allocation={seg}
              dayWidth={dayWidth}
              periodFrom={periodFrom}
              totalDays={totalDays}
              overrideStartIdx={overrideStartIdx}
              overrideEndIdx={overrideEndIdx}
              isMoving={isMoving}
              isResizing={isResizing}
              overrideHours={overrideHours}
              isVresizing={vresizeMatches}
              canEdit={canEditAllocations}
              deleteMode={deleteMode}
              dailyCapacity={dailyCapacity}
              laneHeight={laneHeight}
              laneTopPx={laneTopPxOf(`alloc:${sourceAlloc.instanceId ?? sourceAlloc.id}`)}
              onSelect={() => onSelectAllocation(sourceAlloc, person)}
              onPointerDownBlock={(_a, e) => onAllocationPointerDown(sourceAlloc, e)}
              onPointerDownHandle={onResizeHandlePointerDown}
              onPointerDownVHandle={(alloc, e) =>
                onVresizeHandlePointerDown(alloc, e, { laneHeight, dailyCapacity })
              }
            />
          );
        })}

        {/* TimeOffs — fragmentés par semaine (skip WE) comme les allocations,
            pour matcher le comportement Float (les congés se rendent uniquement
            sur les jours ouvrés). onSelect agit sur le TimeOff source. */}
        {timeOffSegments.filter(inRenderedRange).map((seg) => {
          const source = seg._sourceTimeOff;
          // Ticket #48 — aperçu live du resize vertical (heures/jour).
          const vresizeMatches =
            vresize?.kind === 'timeoff' &&
            (vresize.instanceId
              ? vresize.instanceId === source.instanceId
              : vresize.timeOffId === source.id);
          return (
            <TimeOffBlock
              key={seg._segmentId}
              timeOff={seg}
              dayWidth={dayWidth}
              laneHeight={laneHeight}
              laneTopPx={laneTopPxOf(`to:${source.instanceId ?? source.id}`)}
              periodFrom={periodFrom}
              totalDays={totalDays}
              dailyCapacity={dailyCapacity}
              overrideHours={vresizeMatches ? vresize.currentHours : null}
              isVresizing={vresizeMatches}
              canResize={Boolean(canResizeTimeOff?.(source, person.id))}
              onSelect={() => onSelectTimeOff(source, person)}
              onPointerDownVHandle={(e) =>
                onTimeOffVresizePointerDown(source, e, {
                  laneHeight,
                  dailyCapacity,
                  personId: person.id,
                })
              }
            />
          );
        })}

        {/* Overlay de sélection (drag-to-create uniquement). */}
        {dragGeom ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: dragGeom.left,
              top: BLOCK_VERTICAL_PAD,
              width: dragGeom.width,
              height: laneHeight - BLOCK_VERTICAL_PAD * 2,
              background: 'rgba(50, 100, 150, 0.25)',
              border: '1px dashed var(--accent)',
              borderRadius: 'var(--radius-sm)',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              zIndex: 5,
            }}
          />
        ) : null}
      </div>
    </div>
  );
});

function computeBlockGeometryFromIdx(startIdx, endIdx, dayWidth, totalDays) {
  const clippedStart = Math.max(0, startIdx);
  const clippedEnd = Math.min(totalDays - 1, endIdx);
  if (clippedEnd < 0 || clippedStart >= totalDays) return null;
  const left = clippedStart * dayWidth;
  const width = (clippedEnd - clippedStart + 1) * dayWidth;
  return {
    left,
    width,
    overflowLeft: startIdx < 0,
    overflowRight: endIdx >= totalDays,
  };
}

function computeBlockGeometry(item, dayWidth, periodFrom, totalDays) {
  const start = parseISODate(item.startDate);
  const end = parseISODate(item.endDate);
  const startIdx = diffDays(start, periodFrom);
  const endIdx = diffDays(end, periodFrom);
  return computeBlockGeometryFromIdx(startIdx, endIdx, dayWidth, totalDays);
}

/**
 * Capacité quotidienne moyenne ouvrée d'une Person (en heures).
 *
 * Lit `person.workDaysHours` Json `{monday:8, tuesday:8, ..., sunday:0}` et
 * calcule la moyenne sur les jours travaillés (>0). Si workDaysHours est
 * absent/vide, fallback 8h/jour (standard temps plein).
 *
 * Utilisé pour rendre les blocs Allocation avec une hauteur proportionnelle
 * à la charge : une allocation 2h/j sur une personne qui fait 8h/j de
 * capacité s'affiche en 25% de la hauteur de la rangée.
 */
function computeDailyCapacity(workDaysHours) {
  if (!workDaysHours || typeof workDaysHours !== 'object') return 8;
  const values = Object.values(workDaysHours)
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (values.length === 0) return 8;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

/**
 * Calcule l'offset vertical (ratio 0..1) de chaque allocation pour empiler
 * proprement celles qui se chevauchent dans le temps.
 *
 * Algo simple d'overlap scheduling :
 * - Tri par startDate ASC puis hoursPerDay DESC (les grosses sont posées
 *   en premier, les petites s'empilent au-dessus pour rester lisibles).
 * - Pour chaque alloc, somme les ratios de hauteur des allocs DÉJÀ posées
 *   qui chevauchent ses dates [startDate, endDate] → offset cumulé.
 *
 * Limitations connues :
 * - Si 2 allocs ne chevauchent que partiellement, l'offset est appliqué
 *   sur toute la largeur de la "petite" — acceptable visuellement vs la
 *   complexité d'un calcul par jour.
 * - Pas de réorganisation dynamique pour optimiser l'espace (algo greedy).
 *
 * Retour : Map<allocationId, offsetRatio 0..1>
 */
/**
 * Affecte une "voie" (lane) verticale à chaque allocation ET congé d'une
 * personne, par coloration gloutonne d'intervalles : deux items qui se
 * chevauchent dans le temps tombent sur des voies différentes. Le nombre de
 * voies (`laneCount`) pilote la hauteur de la ligne de la personne — une ligne
 * avec 2 chevauchements simultanés est rendue sur 2× la hauteur de base, etc.
 * (cf. demande utilisateur "doubler la ligne pour voir les 2 schedules").
 *
 * Clés : `alloc:<instanceId|id>` et `to:<instanceId|id>` pour que
 * AllocationBlock / TimeOffBlock retrouvent leur voie.
 */
/** Ticket #91 — jour absolu d'une date ISO, sans objet Date local : ne sert
 *  qu'à comparer des bornes (computeRowLanes est appelé pour toute la fenêtre
 *  chargée de chaque personne, le parsing y dominait). */
function isoDayNumber(d) {
  const x = String(d);
  return Date.UTC(+x.slice(0, 4), +x.slice(5, 7) - 1, +x.slice(8, 10)) / MS_PER_DAY;
}

function computeRowLanes(allocations, timeoffs, dailyCapacity = 8) {
  const cap = Math.max(0.01, dailyCapacity);
  // Hauteur d'un item en unités de capacité (8h = 1). 4h/j → 0.5. Pleine
  // journée → 1. Même formule que les blocs pour un empilement exact.
  const ratioOf = (h) => Math.max(0.1, Math.min(1, (Number(h) || 0) / cap));
  // Les congés pleine journée masquent l'allocation (segments retirés ce
  // jour-là) → pas de voie dédiée, ils se rendent en voie 0. Seuls les congés
  // partiels (4h/j…) coexistent avec une allocation et prennent une voie.
  const items = [
    ...allocations.map((a) => ({
      key: `alloc:${a.instanceId ?? a.id}`,
      s: isoDayNumber(a.startDate),
      e: isoDayNumber(a.endDate),
      r: ratioOf(a.hoursPerDay),
    })),
    ...timeoffs
      .filter((t) => !isFullDayLeave(t))
      .map((t) => ({
        key: `to:${t.instanceId ?? t.id}`,
        s: isoDayNumber(t.startDate),
        e: isoDayNumber(t.endDate),
        r: ratioOf(t.hoursPerDay),
      })),
  ].sort((a, b) => a.s - b.s || a.e - b.e);

  // 1) Affectation gloutonne d'une voie (intervalle coloring) — deux items qui
  //    se chevauchent dans le temps ne partagent jamais une voie.
  const laneEnds = []; // dernière date de fin occupant chaque voie
  const byLane = []; // items de chaque voie, triés par début ET par fin
  const placed = [];
  for (const it of items) {
    let lane = laneEnds.findIndex((end) => end < it.s);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.e);
      byLane.push([]);
    } else {
      laneEnds[lane] = it.e;
    }
    const x = { ...it, lane };
    byLane[lane].push(x);
    placed.push(x);
  }
  // 2) Offset vertical CONTIGU par item : le haut d'un bloc = somme des
  //    hauteurs des blocs des voies inférieures QUI LE CHEVAUCHENT réellement
  //    (pas la hauteur uniforme de la voie sur toute la période). Ainsi un
  //    bloc 4h + un congé 4h le même jour se collent pour remplir 8h, sans
  //    être décalés par des allocations 8h d'AUTRES jours.
  //    Ticket #91 — les items d'une voie ne se chevauchant pas, ils sont triés
  //    par fin comme par début : recherche dichotomique du premier item qui
  //    finit après le début de `x`, puis parcours tant qu'il commence avant sa
  //    fin (au lieu de reparcourir tous les items pour chaque voie).
  const topByKey = new Map();
  let totalRatio = 1;
  for (const x of placed) {
    let top = 0;
    for (let j = 0; j < x.lane; j++) {
      const list = byLane[j];
      let lo = 0;
      let hi = list.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (list[mid].e < x.s) lo = mid + 1;
        else hi = mid;
      }
      let maxR = 0;
      for (let k = lo; k < list.length && list[k].s <= x.e; k++) {
        if (list[k].r > maxR) maxR = list[k].r;
      }
      top += maxR;
    }
    topByKey.set(x.key, top);
    totalRatio = Math.max(totalRatio, top + x.r);
  }
  return {
    laneCount: Math.max(1, laneEnds.length),
    topByKey,
    // Hauteur totale de la ligne en unités de capacité (min 1 = une capacité).
    totalRatio,
  };
}

function AllocationBlock({
  allocation,
  dayWidth,
  periodFrom,
  totalDays,
  overrideStartIdx,
  overrideEndIdx,
  isMoving,
  isResizing,
  overrideHours = null,
  isVresizing = false,
  canEdit,
  /** Ticket #62 — mode Suppression : clic = suppression, pas de drag/resize. */
  deleteMode = false,
  onSelect,
  onPointerDownBlock,
  onPointerDownHandle,
  onPointerDownVHandle,
  /**
   * Capacité quotidienne (heures) de la Person sur laquelle ce bloc est
   * rendu. Sert à calculer la hauteur proportionnelle.
   */
  dailyCapacity = 8,
  /**
   * Hauteur d'une voie (lane) et index de voie de ce bloc. Le bloc est rendu
   * dans sa voie ; la ligne personne a été agrandie pour contenir toutes les
   * voies (voir computeRowLanes).
   */
  laneHeight = ROW_HEIGHT_PX,
  laneTopPx = 0,
}) {
  const geom =
    overrideStartIdx !== null && overrideEndIdx !== null
      ? computeBlockGeometryFromIdx(overrideStartIdx, overrideEndIdx, dayWidth, totalDays)
      : computeBlockGeometry(allocation, dayWidth, periodFrom, totalDays);
  if (!geom) return null;

  // Hauteur proportionnelle à hoursPerDay / capacité (8h = pleine hauteur de
  // voie). Empilé de façon contiguë sous les voies inférieures (laneTopPx).
  const hoursPerDay =
    overrideHours !== null ? overrideHours : Number(allocation.hoursPerDay) || 0;
  const ratio = Math.max(0.1, Math.min(1, hoursPerDay / Math.max(0.01, dailyCapacity)));
  // Ticket #48 — au-delà de la capacité (jusqu'à 12h), le bloc ne peut pas
  // grandir plus que sa voie : un liseré rouge en bas signale le dépassement.
  const overCapacity = hoursPerDay > dailyCapacity;
  // Hauteur pleine de la voie : les blocs d'un même jour se touchent (la pile
  // empilée contiguë), l'espace libre est rejeté en bas de ligne (cf. rowHeights).
  const blockHeight = Math.max(8, Math.round(ratio * laneHeight));
  const blockTop = laneTopPx + BLOCK_VERTICAL_PAD + WL_BAND_HEIGHT;
  const bg = affaireColor(allocation.affaire);
  // Ticket #76 — la tâche est une information de l'allocation au même titre
  // que la phase : elle s'affiche donc à côté d'elle, sur la ligne d'info
  // (`8h/j • Phase • Tâche`), et le titre reste l'affaire.
  //
  // La première version faisait l'inverse — la tâche REMPLAÇAIT le nom de
  // l'affaire dans le titre. En recette, ça ne se lisait pas comme une tâche :
  // on voyait la phase sur la ligne d'info, un titre qui n'était plus l'affaire,
  // et rien qui dise « tâche ». Le repli sur l'ancien libellé libre `name`
  // couvre l'historique non repris.
  const taskLabel = allocation.task?.name?.trim() || allocation.name?.trim() || null;
  const affaireLabel = allocation.affaire?.name || 'Allocation';
  const clientName = allocation.affaire?.client?.name ?? null;
  const tooltipLabel = [affaireLabel, allocation.phase?.name, taskLabel].filter(Boolean).join(' · ');
  // Sprint 8.4 — mode compact pour blocs partiels (ex: 4h/j sur capa 8h →
  // bloc à 36 px ne tient pas 3 lignes). On replie titre + heures sur 1 ligne.
  const isCompact = blockHeight < 48;
  // Mode compact (une seule ligne) : pas de ligne d'info, la tâche suit donc
  // l'affaire dans le titre pour rester visible. Calculé APRÈS `isCompact` —
  // avant, la référence tomberait dans la zone morte temporelle du `const` et
  // le planning planterait au rendu, sans que le build ne le signale.
  const label = isCompact && taskLabel ? `${affaireLabel} · ${taskLabel}` : affaireLabel;
  // Ticket #20 — couleur de texte choisie selon la luminance du fond : sur une
  // couleur d'affaire claire (bleu pastel, gris), le blanc n'était pas lisible.
  const { color: fg, onLight } = readableTextOn(bg);
  const textShadow = onLight ? 'none' : '0 1px 2px rgba(0,0,0,0.35)';
  // Drag / resize seulement en mode Édition et avec les droits.
  const draggable = canEdit && !deleteMode;

  return (
    <div
      data-row-type="allocation"
      data-floatid={allocation.id}
      data-can-edit={draggable ? 'true' : 'false'}
      data-is-moving={isMoving ? 'true' : 'false'}
      data-is-resizing={isResizing ? 'true' : 'false'}
      data-is-vresizing={isVresizing ? 'true' : 'false'}
      data-show-left-edge={geom.overflowLeft ? 'true' : 'false'}
      data-show-right-edge={geom.overflowRight ? 'true' : 'false'}
      onPointerDown={
        draggable
          ? (e) => onPointerDownBlock(allocation, e)
          : (e) => e.stopPropagation()
      }
      onClick={
        // Clic simple sans déplacement (move=null après commit) →
        // commitMove appellera setDetail. Pour USER/VIEWER (sans canEdit) et
        // en mode Suppression, on garde un onClick natif.
        !draggable ? () => onSelect(allocation) : undefined
      }
      style={{
        position: 'absolute',
        left: geom.left,
        top: blockTop,
        width: geom.width - 2,
        height: blockHeight,
        background: bg,
        color: fg,
        border: 'none',
        borderRadius: geom.overflowLeft || geom.overflowRight ? 0 : 'var(--radius-sm)',
        borderTopLeftRadius: geom.overflowLeft ? 0 : 'var(--radius-sm)',
        borderBottomLeftRadius: geom.overflowLeft ? 0 : 'var(--radius-sm)',
        borderTopRightRadius: geom.overflowRight ? 0 : 'var(--radius-sm)',
        borderBottomRightRadius: geom.overflowRight ? 0 : 'var(--radius-sm)',
        padding: blockHeight < 30 ? '2px 6px' : '4px 8px',
        textAlign: 'left',
        boxShadow: overCapacity
          ? 'inset 0 -3px 0 var(--danger), var(--shadow-card)'
          : 'var(--shadow-card)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: blockHeight < 30 ? 'center' : 'flex-start',
        gap: blockHeight < 30 ? 0 : 2,
        zIndex: 4,
        touchAction: draggable ? 'none' : 'auto',
        userSelect: 'none',
        ...(deleteMode ? { cursor: DELETE_CURSOR } : null),
      }}
      title={`${clientName ? `${clientName} — ` : ''}${tooltipLabel} — ${hoursPerDay}h/j sur ${dailyCapacity}h capa${overCapacity ? ' (dépassement)' : ''} (${allocation.startDate.slice(0, 10)} → ${allocation.endDate.slice(0, 10)})`}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          textShadow,
        }}
      >
        {/* Sprint 8 — marqueur série récurrente (data-testid='icon-repeat' Float). */}
        {allocation.isRecurring ? (
          <Repeat size={10} data-testid="icon-repeat" aria-label="Tâche récurrente" />
        ) : null}
        {/* Marqueur occurrence modifiée individuellement (override). */}
        {allocation.isOverride ? (
          <Edit3 size={10} data-testid="icon-edited" aria-label="Occurrence modifiée" />
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{label}</span>
        {/* Mode compact (blocs partiels) : heures collées à droite du titre. */}
        {isCompact ? (
          <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.95, flexShrink: 0 }}>
            {hoursPerDay}h
          </span>
        ) : null}
      </span>
      {/* Layout étendu (blocs >= 48px) : client + heures sur lignes séparées. */}
      {!isCompact && clientName ? (
        <span
          style={{
            fontSize: 10,
            opacity: 0.9,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 500,
            textShadow,
          }}
        >
          {clientName}
        </span>
      ) : null}
      {!isCompact ? (
        <span style={{ fontSize: 11, opacity: 0.9, pointerEvents: 'none', textShadow }}>
          {hoursPerDay}h/j
          {allocation.phase?.name ? ` • ${allocation.phase.name}` : ''}
          {taskLabel ? ` • ${taskLabel}` : ''}
        </span>
      ) : null}
      {/* Sprint 12 — notes visibles quand la densité offre assez de hauteur. */}
      {!isCompact && blockHeight >= 64 && allocation.notes ? (
        <span
          style={{
            fontSize: 10,
            opacity: 0.82,
            pointerEvents: 'none',
            textShadow,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'normal',
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}
        >
          {allocation.notes}
        </span>
      ) : null}

      {/* Poignées de resize (ADMIN uniquement) — invisibles par défaut,
          visibles au hover via CSS .alloc-handle. */}
      {draggable ? (
        <>
          <div
            className="alloc-handle"
            style={{ left: 0 }}
            onPointerDown={(e) => onPointerDownHandle(allocation, 'left', e)}
            aria-label="Redimensionner début"
          />
          <div
            className="alloc-handle"
            style={{ right: 0 }}
            onPointerDown={(e) => onPointerDownHandle(allocation, 'right', e)}
            aria-label="Redimensionner fin"
          />
          {/* Ticket #48 — poignée basse : ajuste les heures/jour (pas paramétré, 12h max). */}
          <div
            className="alloc-handle alloc-handle--v"
            onPointerDown={(e) => onPointerDownVHandle(allocation, e)}
            aria-label="Ajuster les heures par jour"
            title="Glisser pour ajuster les heures par jour"
          />
        </>
      ) : null}
    </div>
  );
}

function TimeOffBlock({
  timeOff,
  dayWidth,
  laneHeight = ROW_HEIGHT_PX,
  laneTopPx = 0,
  periodFrom,
  totalDays,
  dailyCapacity = 8,
  onSelect,
  // Ticket #48 — poignée basse (temps partiel / indisponibilité).
  overrideHours = null,
  isVresizing = false,
  canResize = false,
  onPointerDownVHandle,
}) {
  const geom = computeBlockGeometry(timeOff, dayWidth, periodFrom, totalDays);
  if (!geom) return null;
  const bg = timeOffColor(timeOff.timeOffType);
  // Ticket #20 — texte lisible quel que soit le colorHex du type d'absence.
  const { color: fg, onLight } = readableTextOn(bg);
  // Hauteur proportionnelle à hoursPerDay / capacité quand le TimeOff est
  // partiel (fullDay=false). Cohérent avec AllocationBlock : un congé 4h/j
  // sur capa 8h occupe 50% de la hauteur, un congé pleine journée 100%.
  // Empilé de façon contiguë sous les voies inférieures (laneTopPx).
  // #64 — Une absence bornée à la demi-journée occupe la moitié de la hauteur,
  // comme un congé partiel de 4 h sur une capacité de 8 h : c'est la convention
  // du planning, où la hauteur dit la charge et la largeur les dates. Une
  // absence Gescom est TOUJOURS `fullDay = true` — la demi-journée est portée
  // par startHalf/endHalf — d'où ce cas qui échappait au calcul.
  //
  // Réservé aux absences d'UN SEUL jour : sur plusieurs jours, un même bloc
  // couvre des journées pleines et une borne à la demi-journée, et une hauteur
  // unique ne peut pas dire les deux. La journée pleine l'emporte alors, le
  // libellé (« dep. a.m. ») portant la nuance.
  const singleDay =
    String(timeOff.startDate).slice(0, 10) === String(timeOff.endDate).slice(0, 10);
  // Ticket #48 — pendant le geste, la hauteur suit les heures en cours ;
  // atteindre la capacité du jour revient à une journée entière.
  const previewFullDay = overrideHours !== null && overrideHours >= dailyCapacity;
  const ratio = overrideHours !== null
    ? (previewFullDay ? 1 : Math.max(0.1, overrideHours / Math.max(0.01, dailyCapacity)))
    : timeOff.fullDay
    ? (singleDay ? dayCoverageFactor(timeOff, String(timeOff.startDate).slice(0, 10)) : 1)
    : Math.max(0.1, Math.min(1, (Number(timeOff.hoursPerDay) || 0) / Math.max(0.01, dailyCapacity)));
  // Ticket #20 — lisibilité des congés partiels : un congé court (ex. 2h → 25%
  // de la hauteur) était trop bas pour afficher son libellé. On impose une
  // hauteur minimale lisible (bornée à la voie) et, quand le bloc reste court,
  // on bascule sur un affichage compact **une seule ligne** (type · heures).
  const TIMEOFF_MIN_H = 30;
  const blockHeight = Math.min(
    laneHeight,
    Math.max(TIMEOFF_MIN_H, Math.round(ratio * laneHeight)),
  );
  const compact = blockHeight < 42;
  const blockTop = laneTopPx + BLOCK_VERTICAL_PAD + WL_BAND_HEIGHT;
  const durationLabel = overrideHours !== null
    ? (previewFullDay ? 'Journée' : `${overrideHours}h/j`)
    : timeOff.fullDay
    ? (halfDayLabel(timeOff, { short: true }) ?? 'Journée')
    : `${Number(timeOff.hoursPerDay)}h/j`;
  const statusLabel =
    timeOff.status === 'PENDING' ? ' • en attente' : timeOff.status === 'REJECTED' ? ' • refusé' : '';
  return (
    <button
      type="button"
      data-row-type="timeoff"
      data-floatid={timeOff.id}
      data-status={timeOff.status}
      data-can-resize={canResize ? 'true' : 'false'}
      data-is-vresizing={isVresizing ? 'true' : 'false'}
      onClick={() => onSelect(timeOff)}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: geom.left,
        top: blockTop,
        width: geom.width - 2,
        height: blockHeight,
        background: bg,
        backgroundImage: onLight
          ? `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.10) 4px, rgba(0,0,0,0.10) 8px)`
          : `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.18) 4px, rgba(255,255,255,0.18) 8px)`,
        color: fg,
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        padding: compact ? '2px 6px' : '4px 8px',
        textAlign: 'left',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        alignItems: compact ? 'center' : 'stretch',
        gap: compact ? 5 : 2,
        zIndex: 4,
      }}
      title={`${timeOff.timeOffType?.name ?? 'Absence'} — ${durationLabel}${statusLabel} (${timeOff.startDate.slice(0, 10)} → ${timeOff.endDate.slice(0, 10)})`}
    >
      <span
        style={{
          fontSize: compact ? 11 : 12,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flex: compact ? '1 1 auto' : '0 0 auto',
          minWidth: 0,
          textShadow: onLight ? 'none' : '0 1px 2px rgba(0,0,0,0.35)',
        }}
      >
        {timeOff.isRecurring ? (
          <Repeat size={10} data-testid="icon-repeat" aria-label="Absence récurrente" />
        ) : null}
        {timeOff.isOverride ? (
          <Edit3 size={10} data-testid="icon-edited" aria-label="Occurrence modifiée" />
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {timeOff.timeOffType?.name ?? 'Absence'}
        </span>
      </span>
      {/* Congé partiel court → durée en une ligne compacte (pastille à droite) ;
          sinon 2ᵉ ligne classique. */}
      <span
        style={{
          fontSize: compact ? 10 : 11,
          fontWeight: compact ? 600 : 400,
          opacity: onLight ? 0.8 : 0.95,
          whiteSpace: 'nowrap',
          flex: '0 0 auto',
          textShadow: onLight ? 'none' : '0 1px 2px rgba(0,0,0,0.35)',
        }}
      >
        {durationLabel}
        {statusLabel}
      </span>
      {/* Ticket #48 — poignée basse : ajuste les heures/jour. Le clic qui
          termine le geste ne doit pas ouvrir la fiche du congé. */}
      {canResize ? (
        <span
          className="alloc-handle alloc-handle--v"
          onPointerDown={onPointerDownVHandle}
          onClick={(e) => e.stopPropagation()}
          aria-label="Ajuster les heures par jour"
          title="Glisser pour ajuster les heures par jour"
        />
      ) : null}
    </button>
  );
}

function DetailContent({ detail }) {
  const { type, item, person } = detail;
  const rows =
    type === 'allocation'
      ? [
          ['Personne', displayPersonName(person)],
          ['Client', item.affaire?.client?.name ?? '—'],
          ['Affaire', item.affaire?.name ?? '—'],
          ['Phase', item.phase?.name ?? '—'],
          ['Code', item.affaire?.code ?? '—'],
          ['Période', `${item.startDate.slice(0, 10)} → ${item.endDate.slice(0, 10)}`],
          ['Heure de début', item.startTime ?? 'Journée'],
          ['Heures/jour', `${Number(item.hoursPerDay)}h`],
          ['Statut', item.status],
          ['Facturable', item.billable ? 'Oui' : 'Non'],
          // Ticket #76 — tâche partagée, repli sur le libellé libre historique.
          ['Tâche', item.task?.name ?? (item.name?.trim() || '—')],
        ]
      : [
          ['Personne', displayPersonName(person)],
          ['Type', item.timeOffType?.name ?? '—'],
          ['Période', `${item.startDate.slice(0, 10)} → ${item.endDate.slice(0, 10)}`],
          // Une seule ligne pour la durée : « Heure de début : Journée
          // complète » face à « Heures/jour : Matinée » se contredisait, la
          // première décrivant l'absence de startTime et la seconde les bornes.
          [
            'Durée',
            item.fullDay
              ? (halfDayLabel(item) ?? 'Journée complète')
              : `${Number(item.hoursPerDay)} h par jour`,
          ],
          ...(item.startTime ? [['Heure de début', item.startTime]] : []),
          ['Statut', item.status],
          ['Note de statut', item.statusNote ?? '—'],
          ...(gescomRequestUrl(item.gescomAbsenceId)
            ? [
                [
                  'Demande Gescom',
                  <a
                    key="gescom"
                    href={gescomRequestUrl(item.gescomAbsenceId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Voir la demande dans Gescom"
                    style={{ color: 'var(--accent)' }}
                  >
                    #{item.gescomAbsenceId}
                  </a>,
                ],
              ]
            : []),
        ];
  const notes = item.notes;
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr',
          gap: 6,
          fontSize: 13,
        }}
      >
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <div style={{ color: 'var(--text-muted)' }}>{k}</div>
            <div style={{ color: 'var(--text)' }}>{v}</div>
          </Fragment>
        ))}
      </div>
      {notes ? (
        <div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 4 }}>Notes</div>
          <div
            style={{
              background: 'var(--glass)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              padding: 8,
              fontSize: 13,
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {notes}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------- Modale détail absence (ADMIN — avec suppression) (Sprint 11) ----------

/**
 * Sprint 11 — Modale de détail d'un congé pour l'ADMIN, avec bouton Supprimer.
 *
 * Contrairement au USER (limité à ses demandes PENDING), l'ADMIN peut supprimer
 * (archiver) un congé **quel que soit son statut**, APPROVED inclus — le backend
 * `DELETE /api/timeoffs/:id` n'impose la restriction de statut qu'aux non-ADMIN.
 *
 * Récurrence : si le congé est une série (`isRecurring`), on demande le périmètre
 * (EditScopeDialog) puis on appelle `deleteInstance(scope)` ; sinon archive direct
 * après confirmation.
 */
function TimeOffDetailModal({ open, item, person, canDelete = true, onClose, onDeleted, onError }) {
  const [scopeDialog, setScopeDialog] = useState(null); // { mode: 'delete' } | null
  const [submitting, setSubmitting] = useState(false);

  const performArchive = async (scope) => {
    setSubmitting(true);
    try {
      if (!scope || scope === 'all_occurrences') {
        await apiTimeOffs.archive(item.id);
      } else {
        await apiTimeOffs.deleteInstance(
          item.id,
          item.occurrenceDate ?? item.startDate.slice(0, 10),
          scope,
        );
      }
      onDeleted();
    } catch (e) {
      if (onError) onError(e.message || 'Suppression impossible');
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (item.isRecurring) {
      setScopeDialog({ mode: 'delete' });
      return;
    }
    if (!window.confirm('Supprimer ce congé ? Il disparaîtra du planning.')) return;
    await performArchive(null);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={submitting ? () => {} : onClose}
        title="Détail absence"
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={modalSecondaryBtn(submitting)}
            >
              Fermer
            </button>
            {canDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  ...modalSecondaryBtn(submitting),
                  color: 'var(--danger)',
                  borderColor: 'var(--danger)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Trash2 size={14} /> {submitting ? 'Suppression…' : 'Supprimer'}
              </button>
            ) : null}
          </>
        }
      >
        <DetailContent detail={{ type: 'timeoff', item, person }} />
      </Modal>

      <EditScopeDialog
        open={scopeDialog !== null}
        mode="delete"
        entityLabel="congé"
        onCancel={() => setScopeDialog(null)}
        onConfirm={async (scope) => {
          setScopeDialog(null);
          await performArchive(scope);
        }}
      />
    </>
  );
}

// ---------- Styles formulaires (partagés) ----------

const FIELD_LABEL_STYLE = {
  fontSize: 12,
  color: 'var(--text-muted)',
  marginBottom: 4,
  display: 'block',
  fontWeight: 500,
};

const FIELD_INPUT_STYLE = {
  width: '100%',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  fontSize: 13,
  boxSizing: 'border-box',
};

// Ticket #6 — Lieux de travail planifiables.
const WORK_LOCATIONS = [
  { value: '', label: '— Non renseigné —' },
  { value: 'office', label: 'Bureau' },
  { value: 'client', label: 'Site client' },
  { value: 'remote', label: 'Télétravail' },
  { value: 'other', label: 'Autre' },
];
const WORK_LOCATION_LABELS = {
  office: 'Bureau',
  client: 'Site client',
  remote: 'Télétravail',
  other: 'Autre',
};
const WORK_LOCATION_COLORS = {
  office: '#326496',
  client: '#b26a00',
  remote: '#2e7d32',
  other: '#6b7280',
};
const WORK_LOCATION_SHORT = {
  office: 'Bureau',
  client: 'Client',
  remote: 'Télét.',
  other: 'Autre',
};

// ---------- Modale statut de présence / lieu de travail (ticket #6) ----------

const HALF_OPTIONS = [
  { value: 'full', label: 'Journée' },
  { value: 'morning', label: 'Matin' },
  { value: 'afternoon', label: 'Après-midi' },
];

function StatusModal({ ctx, persons, onClose, onSaved, onError }) {
  const [location, setLocation] = useState('office');
  const [half, setHalf] = useState('full');
  const [recurrence, setRecurrence] = useState({ freq: 'none' });
  const [submitting, setSubmitting] = useState(false);
  // Ticket #42 — pastilles sur desktop, drop-down sur mobile (< 768px) : sous
  // le pouce, 4 pastilles côte à côte deviennent des cibles trop petites.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!ctx) return;
    setLocation(ctx.current?.location ?? 'office');
    // Ticket #26 — À la CRÉATION, la période par défaut est « Journée » (full),
    // quelle que soit la demi-journée cliquée (matin/après-midi) : le besoin
    // métier est une présence pleine journée par défaut. On ne conserve la demi
    // cliquée que pour MODIFIER un statut demi-journée déjà existant.
    setHalf(ctx.current ? (ctx.half ?? 'full') : 'full');
    setRecurrence({ freq: 'none' });
    setSubmitting(false);
  }, [ctx?.personId, ctx?.iso, ctx?.half]);

  if (!ctx) return null;
  const person = persons.find((p) => p.id === ctx.personId);
  const personName = person ? displayPersonName(person) : '';
  // Ticket #6 : sur une journée pleine, on ne peut que MODIFIER le statut
  // existant (pas en ajouter un second) → la période reste « Journée ».
  const periodLocked = ctx.half === 'full';

  const save = async () => {
    setSubmitting(true);
    try {
      await apiWorkLocations.set({
        personId: ctx.personId,
        date: ctx.iso,
        location,
        half,
        recurrenceRule: buildRRuleClient(recurrence),
      });
      onSaved();
    } catch (e) {
      onError(e.message);
      setSubmitting(false);
    }
  };
  const clear = async () => {
    setSubmitting(true);
    try {
      await apiWorkLocations.clear({ personId: ctx.personId, date: ctx.iso, half: ctx.half ?? 'full' });
      onSaved();
    } catch (e) {
      onError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={Boolean(ctx)}
      onClose={submitting ? () => {} : onClose}
      title="Statut de présence"
      subtitle={`${personName} — ${ctx.iso}`}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8 }}>
          <div>
            {ctx.current ? (
              <button type="button" onClick={clear} disabled={submitting} style={{ ...modalSecondaryBtn(submitting), color: 'var(--danger)' }}>
                Effacer
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={submitting} style={modalSecondaryBtn(submitting)}>
              Annuler
            </button>
            <button type="button" onClick={save} disabled={submitting} style={modalPrimaryBtn(submitting)}>
              Enregistrer
            </button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Période</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {HALF_OPTIONS.map((h) => {
              const active = half === h.value;
              const disabled = submitting || (periodLocked && h.value !== 'full');
              return (
                <button
                  key={h.value}
                  type="button"
                  onClick={() => setHalf(h.value)}
                  disabled={disabled}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: 999,
                    border: `1px solid ${active ? 'var(--accent)' : 'var(--glass-border)'}`,
                    background: active ? 'var(--accent)' : 'transparent',
                    color: active ? '#fff' : 'var(--text)',
                    fontSize: 13,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled && !active ? 0.5 : 1,
                  }}
                >
                  {h.label}
                </button>
              );
            })}
          </div>
          {periodLocked ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Journée complète — modifiez le statut existant.
            </div>
          ) : null}
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor={isMobile ? 'status-location' : undefined}>
            Lieu de travail
          </label>
          {/* Ticket #42 — pastilles au lieu d'une drop-down : un clic de moins
              que le <select>, et la couleur reprend celle du lieu dans le
              calendrier (WORK_LOCATION_COLORS) pour la cohérence visuelle.
              Sur mobile on garde le <select> natif (picker plein écran). */}
          {isMobile ? (
            <select
              id="status-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={submitting}
              // #6 dark mode : fond opaque + options stylées pour éviter le
              // « blanc sur blanc » des <option> héritant du thème système.
              style={{ ...FIELD_INPUT_STYLE, background: 'var(--card-bg)' }}
            >
              {WORK_LOCATIONS.filter((w) => w.value).map((w) => (
                <option key={w.value} value={w.value} style={{ background: 'var(--card-bg)', color: 'var(--text)' }}>
                  {w.label}
                </option>
              ))}
            </select>
          ) : (
            <div role="radiogroup" aria-label="Lieu de travail" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {WORK_LOCATIONS.filter((w) => w.value).map((w) => {
                const active = location === w.value;
                const color = WORK_LOCATION_COLORS[w.value] ?? 'var(--accent)';
                return (
                  <button
                    key={w.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={w.label}
                    onClick={() => setLocation(w.value)}
                    disabled={submitting}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1px solid ${active ? color : 'var(--glass-border)'}`,
                      background: active ? color : 'transparent',
                      color: active ? '#fff' : 'var(--text)',
                      fontSize: 13,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting && !active ? 0.5 : 1,
                    }}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 12 }}>
          <RecurrencePicker value={recurrence} onChange={setRecurrence} disabled={submitting} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Sans récurrence, le statut ne s'applique qu'à ce jour.
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Modale de création d'allocation (Sprint 2.C + 2.D phase picker) ----------

function CreateAllocationModal({
  open,
  draft,
  periodFrom,
  persons,
  schedulablePersons = [],
  affaires,
  affairesError,
  canCreateTimeOff = false,
  canCreateAllocation = true,
  canDeclareUnavailability = false,
  unavailabilityForOther = false,
  overbookCtx = null,
  requestOverbookConfirm = null,
  onClose,
  onCreated,
}) {
  // Sprint 11 — onglet actif : 'allocation' (planification projet) ou 'timeoff'
  // (demande de congé directe depuis le calendrier, créée APPROVED par l'ADMIN).
  const [mode, setMode] = useState('allocation');

  const [affaireId, setAffaireId] = useState('');
  // synotis-support#8 — vrai dès que l'utilisateur choisit lui-même l'affaire :
  // la dernière affaire saisie ne l'écrase plus (liste chargée en retard).
  const affaireTouchedRef = useRef(false);
  const [phaseId, setPhaseId] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  // Ticket #76 — { taskId, taskName } : `taskId` pour une tâche existante,
  // `taskName` pour une tâche à créer côté serveur. Jamais les deux.
  const [task, setTask] = useState({ taskId: null, taskName: null });
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('CONFIRMED');
  // Sprint 8 — création avec récurrence optionnelle.
  const [recurrence, setRecurrence] = useState({ freq: 'none' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // synotis-support#10 — personnes ajoutées (une allocation chacune) et
  // personnes déjà servies lors d'une création partiellement en échec.
  const [extraPersonIds, setExtraPersonIds] = useState([]);
  const [donePersonIds, setDonePersonIds] = useState(() => new Set());

  // Phase picker (Sprint 2.D) — chargé dynamiquement quand affaire sélectionnée.
  const [phases, setPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(false);

  // Sprint 11 — état de l'onglet "Congé".
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [toTypeId, setToTypeId] = useState('');
  const [toFullDay, setToFullDay] = useState(true);
  const [toHours, setToHours] = useState('8');
  const [toNotes, setToNotes] = useState('');
  const [toRecurrence, setToRecurrence] = useState({ freq: 'none' });

  // Réinitialise le formulaire à chaque ouverture (nouvelle sélection).
  useEffect(() => {
    if (!open) return;
    // Sans droit de planification, on ouvre directement l'absence que la
    // personne peut poser : indisponibilité pour un sous-traitant, sinon
    // temps partiel.
    setMode(
      canCreateAllocation ? 'allocation' : canDeclareUnavailability ? 'unavailability' : 'timeoff',
    );
    setAffaireId('');
    affaireTouchedRef.current = false;
    setPhaseId('');
    setPhases([]);
    setHoursPerDay('8');
    setTask({ taskId: null, taskName: null });
    setNotes('');
    setStatus('CONFIRMED');
    setRecurrence({ freq: 'none' });
    setToFullDay(true);
    setToHours('8');
    setToNotes('');
    setToRecurrence({ freq: 'none' });
    setExtraPersonIds([]);
    setDonePersonIds(new Set());
    setSubmitError(null);
    setSubmitting(false);
  }, [open, draft?.personId, draft?.startDayIndex, draft?.endDayIndex]);

  // synotis-support#8 — propose la dernière affaire saisie, si elle fait
  // partie des affaires offertes pour cette personne (liste parfois chargée
  // après l'ouverture).
  useEffect(() => {
    if (!open || affaireTouchedRef.current) return;
    const last = lastAffaireIn(affaires);
    if (last) setAffaireId((prev) => prev || last);
  }, [open, affaires, draft?.personId, draft?.startDayIndex, draft?.endDayIndex]);

  // Sprint 11 — charge les types de congés à l'ouverture (pour l'onglet Congé).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiTimeOffTypes
      .list('active')
      .then((data) => {
        if (cancelled) return;
        const list = data?.items ?? [];
        setTimeOffTypes(list);
        // #86 — pré-sélectionne le temps partiel, seul type proposé.
        setToTypeId((prev) => prev || list.find((t) => t.name === PART_TIME_TYPE_NAME)?.id || '');
      })
      .catch(() => {
        if (!cancelled) setTimeOffTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Quand l'Affaire change → reset phaseId + refetch les phases.
  useEffect(() => {
    if (!affaireId) {
      setPhases([]);
      setPhaseId('');
      return;
    }
    let cancelled = false;
    setPhasesLoading(true);
    setPhaseId('');
    apiPhases
      .list(affaireId, 'active')
      .then((data) => {
        if (cancelled) return;
        setPhases(data?.items ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setPhases([]);
      })
      .finally(() => {
        if (cancelled) return;
        setPhasesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [affaireId]);

  if (!open || !draft) return null;

  const person = persons.find((p) => p.id === draft.personId);
  const startDate = addDays(periodFrom, draft.startDayIndex);
  const endDate = addDays(periodFrom, draft.endDayIndex);
  const dayCount = draft.endDayIndex - draft.startDayIndex + 1;
  // synotis-support#10 — une allocation par personne : celle de la ligne
  // d'origine puis les personnes ajoutées, moins celles déjà créées.
  // Après un échec partiel, les personnes déjà servies ne sont plus proposées
  // (une pastille de tag les rajoutait et aurait créé des doublons).
  const extraCandidates = schedulablePersons.filter(
    (p) => p.id !== draft.personId && !donePersonIds.has(p.id),
  );
  const targetPersonIds = [draft.personId, ...extraPersonIds].filter(
    (id) => !donePersonIds.has(id),
  );
  const personLabel = (id) => {
    const p = persons.find((x) => x.id === id);
    return p ? `${p.firstName} ${p.lastName}`.trim() : 'Cette personne';
  };

  const submit = async () => {
    setSubmitError(null);
    if (!affaireId) {
      setSubmitError('Affaire requise.');
      return;
    }
    const hours = Number(hoursPerDay);
    // Ticket #11 : durée en heures entières ; #48 : jusqu'à 12h.
    if (!Number.isInteger(hours) || hours < 1 || hours > MAX_HOURS_PER_DAY) {
      setSubmitError(`Heures/jour : un entier entre 1 et ${MAX_HOURS_PER_DAY}.`);
      return;
    }

    const doCreate = async () => {
      setSubmitting(true);
      // Sprint 8.3 — facturabilité héritée automatiquement de l'Affaire :
      // une affaire `nonBillable=true` produit des allocations non-facturables
      // sans intervention du PM/ADMIN. La décision se prend au niveau projet.
      const selectedAffaire = affaires.find((a) => a.id === affaireId);
      const inheritedBillable = selectedAffaire?.nonBillable !== true;
      // synotis-support#10 — une requête par personne (route unitaire : les
      // droits sont revérifiés personne par personne) ; on poursuit en cas
      // d'échec et on ne garde dans la modale que les personnes en échec.
      const done = new Set(donePersonIds);
      const failures = [];
      for (const personId of targetPersonIds) {
        try {
          await apiAllocations.create({
            personId,
            affaireId,
            phaseId: phaseId || null,
            startDate: toISODate(startDate),
            endDate: toISODate(endDate),
            hoursPerDay: hours,
            taskId: task.taskId,
            taskName: task.taskName,
            notes: notes.trim() || null,
            billable: inheritedBillable,
            status,
            // Sprint 8 — RRULE optionnelle (null si freq='none').
            recurrenceRule: buildRRuleClient(recurrence),
          });
          done.add(personId);
        } catch (e) {
          failures.push(`${personLabel(personId)} : ${e.message || 'création impossible'}`);
        }
      }
      setSubmitting(false);
      if (done.size > donePersonIds.size) rememberLastAffaire(affaireId);
      if (failures.length === 0) {
        onCreated();
        return;
      }
      setDonePersonIds(done);
      setExtraPersonIds((prev) => prev.filter((id) => !done.has(id)));
      setSubmitError(
        targetPersonIds.length === 1
          ? failures[0].replace(/^.+? : /, '')
          : done.size > donePersonIds.size
          ? `Allocation créée pour ${done.size - donePersonIds.size} personne(s). Échec pour : ${failures.join(' · ')}`
          : `Échec pour : ${failures.join(' · ')}`,
      );
    };

    // Sprint 24 — surbooking : confirmation explicite (non bloquante backend).
    // On ne contrôle que la 1ʳᵉ occurrence [startDate, endDate] (cohérent avec
    // le reste — la récurrence est validée à l'usage). Si le contexte n'est pas
    // disponible, on procède sans modale (robustesse).
    if (overbookCtx && requestOverbookConfirm) {
      // synotis-support#10 — une seule confirmation pour toutes les personnes.
      const overbooked = targetPersonIds
        .map((personId) => ({
          personId,
          days: computeOverbookedDays({
            personId,
            startIso: toISODate(startDate),
            endIso: toISODate(endDate),
            newHoursPerDay: hours,
            ctx: overbookCtx,
          }),
        }))
        .filter((x) => x.days.length > 0);
      if (overbooked.length > 0) {
        const multi = targetPersonIds.length > 1;
        requestOverbookConfirm({
          days: overbooked.flatMap((x) =>
            x.days.map((d) => (multi ? { ...d, personName: personLabel(x.personId) } : d)),
          ),
          personName:
            overbooked.length === 1
              ? personLabel(overbooked[0].personId)
              : `${overbooked.length} personnes`,
          plural: overbooked.length > 1,
          onConfirm: doCreate,
        });
        return;
      }
    }
    await doCreate();
  };

  // Sprint 11 — création d'un congé depuis le calendrier (ADMIN, seul à
  // disposer du drag-to-create). #86 — seul le temps partiel est encore
  // créable ici (les autres congés se déposent dans Gescom) ; le serveur le
  // pose directement APPROVED, l'approve ci-dessous n'est qu'un filet.
  const submitTimeOff = async () => {
    setSubmitError(null);
    const isUnavailability = mode === 'unavailability';
    const typeId = isUnavailability
      ? timeOffTypes.find((t) => t.name === UNAVAILABILITY_TYPE_NAME)?.id
      : toTypeId;
    if (!typeId) {
      setSubmitError(
        isUnavailability
          ? "Le type « Indisponibilité » n'est pas configuré — contactez un administrateur."
          : 'Type de congé requis.',
      );
      return;
    }
    let hours = 0;
    if (!toFullDay) {
      hours = Number(toHours);
      if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
        setSubmitError('Heures/jour invalide (entre 0 exclu et 24).');
        return;
      }
    }
    setSubmitting(true);
    try {
      const created = await apiTimeOffs.create({
        personIds: [draft.personId],
        timeOffTypeId: typeId,
        startDate: toISODate(startDate),
        endDate: toISODate(endDate),
        hoursPerDay: toFullDay ? 0 : hours,
        fullDay: toFullDay,
        notes: toNotes.trim() || null,
        recurrenceRule: buildRRuleClient(toRecurrence),
      });
      // Validation immédiate (ADMIN agit comme valideur en posant dans le planning).
      // Une indisponibilité est déjà validée par le serveur : rien à approuver.
      if (!isUnavailability && created.status !== 'APPROVED') {
        try {
          await apiTimeOffs.approve(created.id, 'Créé et validé depuis le planning');
        } catch {
          // Le congé est créé (PENDING) même si l'approbation échoue — non bloquant.
        }
      }
      onCreated();
    } catch (e) {
      setSubmitError(e.message || 'Création impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const isUnavailabilityMode = mode === 'unavailability';
  // L'indisponibilité réutilise le formulaire congé, type imposé.
  const isTimeOff = mode === 'timeoff' || isUnavailabilityMode;
  const tabs = [
    ...(canCreateAllocation ? [{ key: 'allocation', label: 'Allocation' }] : []),
    ...(canCreateTimeOff ? [{ key: 'timeoff', label: 'Temps partiel' }] : []),
    ...(canDeclareUnavailability ? [{ key: 'unavailability', label: 'Indisponibilité' }] : []),
  ];

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={
        isUnavailabilityMode
          ? 'Déclarer une indisponibilité'
          : isTimeOff
          ? 'Déclarer un temps partiel'
          : 'Créer une allocation'
      }
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={modalSecondaryBtn(submitting)}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={isTimeOff ? submitTimeOff : submit}
            disabled={submitting}
            style={modalPrimaryBtn(submitting)}
          >
            {submitting
              ? 'Création…'
              : isUnavailabilityMode
              ? "Déclarer l'indisponibilité"
              : isTimeOff
              ? 'Créer le congé'
              : targetPersonIds.length > 1
              ? `Créer (${targetPersonIds.length})`
              : 'Créer'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Sprint 11 — sélecteur d'onglet Allocation / Congé (pills).
            Sprint 16 — l'onglet Congé (création APPROVED immédiate) reste réservé
            à l'ADMIN ; masqué pour les non-ADMIN en édition self-service. */}
        <div style={{ display: 'flex', gap: 8 }} hidden={tabs.length < 2}>
          {tabs.map((tab) => {
            const active = mode === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (!submitting) {
                    setMode(tab.key);
                    setSubmitError(null);
                  }
                }}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-pill)',
                  border: '1px solid',
                  borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                  background: active ? 'var(--accent)' : 'transparent',
                  color: active ? '#fff' : 'var(--text-muted)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {submitError ? <ErrorBlock>{submitError}</ErrorBlock> : null}
        {!isTimeOff && affairesError ? (
          <WarningBlock>
            Impossible de charger les affaires : {affairesError}
          </WarningBlock>
        ) : null}

        <div style={recapBoxStyle}>
          <div style={{ color: 'var(--text-muted)' }}>Personne</div>
          <div style={{ color: 'var(--text)', fontWeight: 500 }}>
            {displayPersonName(person)}
            {donePersonIds.has(draft.personId) ? (
              <span style={{ color: 'var(--success)', fontWeight: 400, marginLeft: 8 }}>
                (créée)
              </span>
            ) : null}
            {!isTimeOff && extraPersonIds.length > 0 ? (
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
                + {extraPersonIds.length} autre{extraPersonIds.length > 1 ? 's' : ''}
              </span>
            ) : null}
          </div>
          <div style={{ color: 'var(--text-muted)' }}>Période</div>
          <div style={{ color: 'var(--text)' }}>
            {toISODate(startDate)} → {toISODate(endDate)}
            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
              ({dayCount} jour{dayCount > 1 ? 's' : ''})
            </span>
          </div>
        </div>

        {!isTimeOff && extraCandidates.length > 0 ? (
          <div>
            <div style={FIELD_LABEL_STYLE}>Autres personnes (optionnel)</div>
            <PersonMultiPicker
              candidates={extraCandidates}
              value={extraPersonIds}
              onChange={setExtraPersonIds}
              disabled={submitting}
              selectStyle={FIELD_INPUT_STYLE}
            />
          </div>
        ) : null}

        {!isTimeOff && (
        <>
        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="alloc-affaire">
            Affaire <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <AffaireFilterableSelect
            id="alloc-affaire"
            affaires={affaires}
            value={affaireId}
            onChange={(next) => {
              affaireTouchedRef.current = true;
              setAffaireId(next);
              // Ticket #76 — une tâche appartient à une seule affaire : la
              // conserver en changeant d'affaire ferait rejeter le payload.
              setTask({ taskId: null, taskName: null });
            }}
            disabled={submitting}
            selectStyle={FIELD_INPUT_STYLE}
          />
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="alloc-phase">
            Phase
          </label>
          <select
            id="alloc-phase"
            value={phaseId}
            onChange={(e) => setPhaseId(e.target.value)}
            disabled={submitting || !affaireId || phasesLoading}
            style={FIELD_INPUT_STYLE}
          >
            <option value="">
              {!affaireId
                ? '— Sélectionner une affaire d\'abord —'
                : phasesLoading
                ? 'Chargement…'
                : '(aucune)'}
            </option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="alloc-hours">
              Heures / jour
            </label>
            <input
              id="alloc-hours"
              type="number"
              step="1"
              min="1"
              max={MAX_HOURS_PER_DAY}
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(e.target.value)}
              disabled={submitting}
              style={FIELD_INPUT_STYLE}
            />
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="alloc-status">
              Statut
            </label>
            <select
              id="alloc-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={submitting}
              style={FIELD_INPUT_STYLE}
            >
              <option value="CONFIRMED">Confirmé</option>
              <option value="TENTATIVE">Provisoire</option>
              <option value="COMPLETED">Terminé</option>
            </select>
          </div>
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="alloc-task">
            Tâche (optionnel)
          </label>
          <TaskPicker
            inputId="alloc-task"
            affaireId={affaireId}
            value={task}
            onChange={setTask}
            disabled={submitting || !affaireId}
            style={FIELD_INPUT_STYLE}
          />
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="alloc-notes">
            Notes (optionnel)
          </label>
          <textarea
            id="alloc-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            rows={3}
            style={{ ...FIELD_INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {/* Sprint 8 — récurrence optionnelle */}
        <div
          style={{
            borderTop: '1px solid var(--glass-border)',
            paddingTop: 12,
            marginTop: 4,
          }}
        >
          <RecurrencePicker value={recurrence} onChange={setRecurrence} disabled={submitting} />
        </div>
        </>
        )}

        {isTimeOff && (
        <>
          {isUnavailabilityMode ? (
            <div
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--glass-border)',
                background: 'var(--glass)',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              {unavailabilityForOther ? (
                <>
                  Signale les jours où {person ? `${person.firstName} ${person.lastName}` : 'ce sous-traitant'}{' '}
                  n'est pas disponible pour Synotis. Validée d'office, elle retire ces jours du
                  planning comme un temps partiel ; elle reste supprimable depuis le planning.
                </>
              ) : (
                <>
                  Signale les jours où vous n'êtes pas disponible pour Synotis. Validée
                  d'office, elle retire ces jours du planning comme un temps partiel ; vous
                  pouvez la supprimer à tout moment depuis le planning.
                </>
              )}
            </div>
          ) : (
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="to-type">
              Type <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              Les congés se déposent dans Gescom ; seul le temps partiel se déclare ici (validé
              automatiquement).
            </div>
            <select
              id="to-type"
              value={toTypeId}
              onChange={(e) => setToTypeId(e.target.value)}
              disabled={submitting}
              style={FIELD_INPUT_STYLE}
            >
              <option value="">— Sélectionner —</option>
              {timeOffTypes.filter((t) => t.name === PART_TIME_TYPE_NAME).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
            >
              <input
                type="checkbox"
                checked={toFullDay}
                onChange={(e) => setToFullDay(e.target.checked)}
                disabled={submitting}
              />
              Jour entier
            </label>
          </div>

          {!toFullDay && (
            <div>
              <label style={FIELD_LABEL_STYLE} htmlFor="to-hours">
                Heures / jour
              </label>
              <input
                id="to-hours"
                type="number"
                step="0.25"
                min="0"
                max="24"
                value={toHours}
                onChange={(e) => setToHours(e.target.value)}
                disabled={submitting}
                style={FIELD_INPUT_STYLE}
              />
            </div>
          )}

          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="to-notes">
              Notes (optionnel)
            </label>
            <textarea
              id="to-notes"
              value={toNotes}
              onChange={(e) => setToNotes(e.target.value)}
              disabled={submitting}
              rows={3}
              style={{ ...FIELD_INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div
            style={{
              borderTop: '1px solid var(--glass-border)',
              paddingTop: 12,
              marginTop: 4,
            }}
          >
            <RecurrencePicker value={toRecurrence} onChange={setToRecurrence} disabled={submitting} />
          </div>

          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            Le congé sera créé et validé immédiatement (statut Approuvé).
          </div>
        </>
        )}
      </div>
    </Modal>
  );
}

// ---------- Modale d'édition d'allocation (Sprint 2.D) ----------

function EditAllocationModal({
  open,
  allocation,
  person,
  affaires,
  affairesError,
  onClose,
  onSaved,
  onError,
}) {
  const [affaireId, setAffaireId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('8');
  // Ticket #76 — { taskId, taskName } : `taskId` pour une tâche existante,
  // `taskName` pour une tâche à créer côté serveur. Jamais les deux.
  const [task, setTask] = useState({ taskId: null, taskName: null });
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('CONFIRMED');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Sprint 8 — récurrence + dialog d'EditScope.
  const [recurrence, setRecurrence] = useState({ freq: 'none' });
  const [scopeDialog, setScopeDialog] = useState(null); // { mode: 'edit' | 'delete' }

  const [phases, setPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(false);

  // Pré-remplit le formulaire à chaque ouverture (nouvelle allocation).
  useEffect(() => {
    if (!open || !allocation) return;
    setAffaireId(allocation.affaireId ?? '');
    setPhaseId(allocation.phaseId ?? '');
    setStartDate(allocation.startDate?.slice(0, 10) ?? '');
    setEndDate(allocation.endDate?.slice(0, 10) ?? '');
    setHoursPerDay(String(allocation.hoursPerDay ?? '8'));
    // `allocation.name` (libellé libre historique) sert de repli d'affichage
    // tant que l'allocation n'a pas été reprise en tâche partagée.
    setTask({
      taskId: allocation.taskId ?? null,
      taskName: allocation.task?.name ?? allocation.name ?? null,
    });
    setNotes(allocation.notes ?? '');
    setStatus(allocation.status ?? 'CONFIRMED');
    // Sprint 8 — pré-parser la RRULE existante (si série récurrente).
    setRecurrence(parseRRuleClient(allocation.recurrenceRule ?? null));
    setSubmitError(null);
    setSubmitting(false);
  }, [open, allocation?.id, allocation?.recurrenceRule]);

  // Quand l'Affaire change → refetch les phases (sans reset phaseId si on
  // n'a pas encore changé d'affaire vs allocation d'origine).
  const originalAffaireId = allocation?.affaireId;
  useEffect(() => {
    if (!affaireId) {
      setPhases([]);
      return;
    }
    let cancelled = false;
    setPhasesLoading(true);
    apiPhases
      .list(affaireId, 'active')
      .then((data) => {
        if (cancelled) return;
        setPhases(data?.items ?? []);
        // Reset phaseId si l'affaire diffère de l'originale.
        if (affaireId !== originalAffaireId) {
          setPhaseId('');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPhases([]);
      })
      .finally(() => {
        if (cancelled) return;
        setPhasesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [affaireId, originalAffaireId]);

  if (!open || !allocation) return null;

  // Sprint 8.3 — `billable` retiré du payload : la facturabilité hérite
  // de l'Affaire et n'est plus éditable depuis cette modale. Le champ
  // existant en BDD reste inchangé (PATCH partiel, schema Zod en
  // `.partial()`).
  // Ticket #92 — `allocation` est l'OCCURRENCE cliquée, pas la série : ses
  // dates sont celles de l'occurrence. Pour « toute la série », on ne touche
  // donc aux bornes de la série que si l'utilisateur a modifié les dates (en
  // décalant la série d'autant), et à la règle que s'il a modifié le motif —
  // auparavant la règle repartait à null et la série redémarrait à la date
  // cliquée.
  const instanceStart = allocation.startDate?.slice(0, 10) ?? '';
  const instanceEnd = allocation.endDate?.slice(0, 10) ?? '';
  const recurrenceChanged =
    JSON.stringify(recurrence) !==
    JSON.stringify(parseRRuleClient(allocation.recurrenceRule ?? null));
  const seriesDates = () => {
    if (!allocation.isRecurring) return { startDate, endDate };
    if (startDate === instanceStart && endDate === instanceEnd) return {};
    const DAY = 86_400_000;
    const shift = Date.parse(startDate) - Date.parse(instanceStart);
    const seriesStart = Date.parse(allocation.seriesStartDate ?? instanceStart) + shift;
    const span = Date.parse(endDate) - Date.parse(startDate);
    const iso = (ms) => new Date(Math.round(ms / DAY) * DAY).toISOString().slice(0, 10);
    return { startDate: iso(seriesStart), endDate: iso(seriesStart + span) };
  };
  const buildPayload = () => ({
    affaireId,
    phaseId: phaseId || null,
    ...seriesDates(),
    hoursPerDay: Number(hoursPerDay),
    taskId: task.taskId,
    taskName: task.taskName,
    notes: notes.trim() || null,
    status,
    // Sprint 8 — RRULE générée depuis le picker (null si freq='none').
    ...(recurrenceChanged ? { recurrenceRule: buildRRuleClient(recurrence) } : {}),
  });

  // Sprint 8 — Pour le mode `this_occurrence`/`this_and_future`, on n'envoie
  // que les champs utiles à un override individuel (pas la RRULE elle-même).
  // Sprint 8.3 — On envoie aussi `movedToDate` (si décalage vs occurrenceDate
  // d'origine) et `durationDays` (calculé depuis startDate/endDate locaux)
  // pour permettre de déplacer/redimensionner une occurrence individuelle.
  const buildInstancePayload = () => {
    const originalOccDate =
      allocation?.occurrenceDate ??
      (allocation?.startDate ? allocation.startDate.slice(0, 10) : startDate);
    const payload = {
      phaseId: phaseId || null,
      hoursPerDay: Number(hoursPerDay),
      taskId: task.taskId,
      taskName: task.taskName,
      notes: notes.trim() || null,
      status,
      // Ticket #92 — l'affaire n'était pas transmise : changer d'affaire
      // « pour cette occurrence » ou « et les suivantes » restait sans effet.
      ...(affaireId && affaireId !== allocation.affaireId ? { affaireId } : {}),
    };
    if (startDate && startDate !== originalOccDate) {
      payload.movedToDate = startDate;
    }
    if (startDate && endDate && endDate >= startDate) {
      const days = Math.round(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000,
      ) + 1;
      if (days >= 1) payload.durationDays = days;
    }
    return payload;
  };

  const performSave = async (scope) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (!scope || scope === 'all_occurrences') {
        await apiAllocations.update(allocation.id, buildPayload());
      } else {
        await apiAllocations.patchInstance(
          allocation.id,
          allocation.occurrenceDate ?? allocation.startDate.slice(0, 10),
          scope,
          buildInstancePayload(),
        );
      }
      onSaved();
    } catch (e) {
      setSubmitError(e.message || 'Enregistrement impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    setSubmitError(null);
    if (!affaireId) {
      setSubmitError('Affaire requise.');
      return;
    }
    if (!startDate || !endDate) {
      setSubmitError('Dates requises.');
      return;
    }
    if (startDate > endDate) {
      setSubmitError('Date de fin antérieure à la date de début.');
      return;
    }
    const hours = Number(hoursPerDay);
    // Ticket #11 : durée en heures entières ; #48 : jusqu'à 12h.
    if (!Number.isInteger(hours) || hours < 1 || hours > MAX_HOURS_PER_DAY) {
      setSubmitError(`Heures/jour : un entier entre 1 et ${MAX_HOURS_PER_DAY}.`);
      return;
    }
    // Sprint 8 — Si série récurrente, demander le périmètre.
    if (allocation.isRecurring) {
      setScopeDialog({ mode: 'edit' });
      return;
    }
    await performSave(null);
  };

  const performArchive = async (scope) => {
    setSubmitting(true);
    try {
      if (!scope || scope === 'all_occurrences') {
        await apiAllocations.archive(allocation.id);
      } else {
        await apiAllocations.deleteInstance(
          allocation.id,
          allocation.occurrenceDate ?? allocation.startDate.slice(0, 10),
          scope,
        );
      }
      onSaved();
    } catch (e) {
      if (onError) onError(e.message || 'Archivage impossible');
      setSubmitting(false);
    }
  };

  const archive = async () => {
    if (allocation.isRecurring) {
      setScopeDialog({ mode: 'delete' });
      return;
    }
    if (!window.confirm('Archiver cette allocation ? Elle disparaîtra du planning.')) return;
    await performArchive(null);
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Modifier l'allocation"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={archive}
            disabled={submitting}
            title="Archiver l'allocation (soft-delete)"
            style={{
              background: 'transparent',
              color: 'var(--danger)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 14px',
              fontSize: 13,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginRight: 'auto',
            }}
          >
            <Trash2 size={14} />
            Supprimer
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={modalSecondaryBtn(submitting)}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={modalPrimaryBtn(submitting)}
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {submitError ? <ErrorBlock>{submitError}</ErrorBlock> : null}
        {affairesError ? (
          <WarningBlock>
            Impossible de charger les affaires : {affairesError}
          </WarningBlock>
        ) : null}

        <div style={recapBoxStyle}>
          <div style={{ color: 'var(--text-muted)' }}>Personne</div>
          <div style={{ color: 'var(--text)', fontWeight: 500 }}>
            {displayPersonName(person)}
          </div>
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="edit-affaire">
            Affaire <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <AffaireFilterableSelect
            id="edit-affaire"
            affaires={affaires}
            value={affaireId}
            onChange={(next) => {
              setAffaireId(next);
              // Ticket #76 — une tâche appartient à une seule affaire : la
              // conserver en changeant d'affaire ferait rejeter le payload.
              setTask({ taskId: null, taskName: null });
            }}
            disabled={submitting}
            selectStyle={FIELD_INPUT_STYLE}
          />
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="edit-phase">
            Phase
          </label>
          <select
            id="edit-phase"
            value={phaseId}
            onChange={(e) => setPhaseId(e.target.value)}
            disabled={submitting || !affaireId || phasesLoading}
            style={FIELD_INPUT_STYLE}
          >
            <option value="">
              {!affaireId
                ? '— Sélectionner une affaire d\'abord —'
                : phasesLoading
                ? 'Chargement…'
                : '(aucune)'}
            </option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="edit-start">
              Début <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <DatePicker
              id="edit-start"
              value={startDate}
              onChange={setStartDate}
              disabled={submitting}
              required
              style={FIELD_INPUT_STYLE}
            />
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="edit-end">
              Fin <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <DatePicker
              id="edit-end"
              value={endDate}
              onChange={setEndDate}
              disabled={submitting}
              required
              style={FIELD_INPUT_STYLE}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="edit-hours">
              Heures / jour
            </label>
            <input
              id="edit-hours"
              type="number"
              step="1"
              min="1"
              max={MAX_HOURS_PER_DAY}
              value={hoursPerDay}
              onChange={(e) => setHoursPerDay(e.target.value)}
              disabled={submitting}
              style={FIELD_INPUT_STYLE}
            />
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE} htmlFor="edit-status">
              Statut
            </label>
            <select
              id="edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={submitting}
              style={FIELD_INPUT_STYLE}
            >
              <option value="CONFIRMED">Confirmé</option>
              <option value="TENTATIVE">Provisoire</option>
              <option value="COMPLETED">Terminé</option>
            </select>
          </div>
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="edit-task">
            Tâche (optionnel)
          </label>
          <TaskPicker
            inputId="edit-task"
            affaireId={affaireId}
            value={task}
            onChange={setTask}
            disabled={submitting || !affaireId}
            style={FIELD_INPUT_STYLE}
          />
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE} htmlFor="edit-notes">
            Notes (optionnel)
          </label>
          <textarea
            id="edit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            rows={3}
            style={{ ...FIELD_INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>

        {/* Sprint 8 — picker récurrence + dialog d'EditScope */}
        <div
          style={{
            borderTop: '1px solid var(--glass-border)',
            paddingTop: 12,
            marginTop: 4,
          }}
        >
          <RecurrencePicker value={recurrence} onChange={setRecurrence} disabled={submitting} />
          {allocation.isRecurring ? (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              Cette allocation est récurrente — la modification du motif s'applique à toute la série.
            </p>
          ) : null}
        </div>
      </div>

      <EditScopeDialog
        open={scopeDialog !== null}
        mode={scopeDialog?.mode ?? 'edit'}
        entityLabel="allocation"
        onCancel={() => setScopeDialog(null)}
        onConfirm={async (scope) => {
          const mode = scopeDialog?.mode;
          setScopeDialog(null);
          if (mode === 'delete') await performArchive(scope);
          else await performSave(scope);
        }}
      />
    </Modal>
  );
}

// ---------- Styles partagés modales ----------

const modalPrimaryBtn = (disabled) => ({
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const modalSecondaryBtn = (disabled) => ({
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 14px',
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const recapBoxStyle = {
  background: 'var(--glass)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 12,
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: 6,
  fontSize: 13,
};

function ErrorBlock({ children }) {
  return (
    <div
      style={{
        background: 'rgba(220, 53, 69, 0.1)',
        border: '1px solid var(--danger)',
        color: 'var(--danger)',
        borderRadius: 'var(--radius-sm)',
        padding: '8px 12px',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

function WarningBlock({ children }) {
  return (
    <div
      style={{
        background: 'rgba(255, 193, 7, 0.1)',
        border: '1px solid var(--warning)',
        color: 'var(--warning)',
        borderRadius: 'var(--radius-sm)',
        padding: '8px 12px',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
